import {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import {useAuth} from '../../contexts/AuthContext';
import { API_BASE_URL, deleteUserAiProviderKey, fetchAiRuntimeStatus, getApiBaseForOAuthAndRedirects, getAuthHeaders, getUserGoogleAuthStatus, getUserGoogleAuthUrl, disconnectUserGoogleAuth, getUserGoogleCredentials, importUserGoogleCredentials, listAiCatalogModels, listAiProviderModels, listAiProviders, listUserActiveModelOptionsForProvider, listUserAiProviderKeys, listUserGoogleCredentialImports, saveUserAiProviderKey, validateUserAiProviderKey, saveUserGoogleCredentials } from '../../utils/api';
import { getRootPath, getDomain } from '../../utils/config';
import { useGroup } from '../../contexts/GroupContext';
import { WEB_SEARCH_PROVIDERS, isFeelLuckyEnabled } from '../../utils/webSearchProviders';
import {
    RESEARCH_TIMEOUT_DEFAULT_MINUTES,
    RESEARCH_TIMEOUT_MAX_MINUTES,
    RESEARCH_TIMEOUT_MIN_MINUTES,
} from '../../utils/researchDefaults';
import {
    CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT,
    CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_MAX,
    CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_MIN,
} from '../../utils/chatToolResultDefaults';

const LLM_PROVIDER_KEY_ALIASES = {
    google: ['GOOGLE_KEY', 'GEMINI_KEY'],
    gemini: ['GOOGLE_KEY', 'GEMINI_KEY'],
};

const LLM_PROVIDER_SLUG_ALIASES = {
    google: ['google', 'gemini'],
    gemini: ['google', 'gemini'],
};

export function useSettingsPageState(initialTab = null) {
    const {user, toggleDarkMode, darkMode, chatMarkdownEnabled, toggleChatMarkdown} = useAuth();
    // If initialTab is provided, use it and don't allow switching. Otherwise default to 'system' and allow switching.
    const [activeTab, setActiveTab] = useState(initialTab || 'system');
    
    // Sync activeTab with initialTab prop changes
    useEffect(() => {
        if (initialTab !== null && initialTab !== undefined) {
            setActiveTab(initialTab);
        }
    }, [initialTab]);
    // Access group context (no direct usage needed here after UI move)
    // eslint-disable-next-line no-unused-vars
    const groupCtx = useGroup();

    // System Settings (global, not user-specific)
    const [systemSettings, setSystemSettings] = useState({
        embeddingModel: '',
        aiRuntime: 'aisdk',
        aiRuntimeHealth: null,
        aiRuntimeStatus: 'unknown',
        aiProviders: [],
        aiProviderModels: []
    });
    const [originalSystemSettings, setOriginalSystemSettings] = useState({
        embeddingModel: '',
        aiRuntime: 'aisdk',
        aiRuntimeHealth: null,
        aiRuntimeStatus: 'unknown',
        aiProviders: [],
        aiProviderModels: []
    });

    // User Settings (per-user)
    const [userSettings, setUserSettings] = useState({
        openaiKey: '',
        geminiKey: '',
        anthropicKey: '',
        llm_keys: {},
        web_search_keys: {},
        feelLuckyMcpServerId: '',
        pansynapseUserId: '',
        activeModelId: '',
        activeModelProvider: '',
        activeWebSearchProvider: '',
        researchTimeoutMinutes: RESEARCH_TIMEOUT_DEFAULT_MINUTES,
        chatToolResultMaxFieldLength: CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT,
    });
    const [originalUserSettings, setOriginalUserSettings] = useState({
        openaiKey: '',
        geminiKey: '',
        anthropicKey: '',
        llm_keys: {},
        web_search_keys: {},
        feelLuckyMcpServerId: '',
        pansynapseUserId: '',
        activeModelId: '',
        activeModelProvider: '',
        activeWebSearchProvider: '',
        researchTimeoutMinutes: RESEARCH_TIMEOUT_DEFAULT_MINUTES,
        chatToolResultMaxFieldLength: CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT,
    });
    const [providers, setProviders] = useState([]);
    const [activeModelOptions, setActiveModelOptions] = useState([]);
    const draftProviderModelsRequestRef = useRef(0);
    const [userProviderKeysApiAvailable, setUserProviderKeysApiAvailable] = useState(false);
    // Provider support check state: { [providerId]: { loading: boolean, supported: boolean|null } }
    const [providerSupport, setProviderSupport] = useState({});
    // Provider validation state: { [providerId]: { loading: boolean, status: string|null, error: string|null } }
    const [providerValidation] = useState({});
    const [message, setMessage] = useState({type: '', text: ''});
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [personalGoogleAuth, setPersonalGoogleAuth] = useState({
        isConnected: false,
        connectedUser: null,
        loading: true,
        activeSource: null,
        systemConnected: false,
        canAccessDrive: false,
        driveAccessFailureReason: null,
    });
    const [showPersonalDisconnectConfirm, setShowPersonalDisconnectConfirm] = useState(false);
    const [userGoogleCredentials, setUserGoogleCredentials] = useState({
        client_id: '',
        client_secret: '',
        redirect_uri: '',
        hasCredentials: false,
        loading: false,
    });
    const [userGoogleCredentialImports, setUserGoogleCredentialImports] = useState([]);
    const [selectedGoogleCredentialImportId, setSelectedGoogleCredentialImportId] = useState('');
    const [userGoogleCredentialImportsLoading, setUserGoogleCredentialImportsLoading] = useState(false);
    const [userGoogleCredentialImporting, setUserGoogleCredentialImporting] = useState(false);
    const [userGoogleCredentialsSaving, setUserGoogleCredentialsSaving] = useState(false);
    const [entryToDelete, setEntryToDelete] = useState(null);
    const [systemSettingsLoading, setSystemSettingsLoading] = useState(true);
    const [userSettingsLoading, setUserSettingsLoading] = useState(true);
    const [dataSettingsLoading, setDataSettingsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dataEntries, setDataEntries] = useState([]);
    const [originalDataEntries, setOriginalDataEntries] = useState([]);
    const [dataEntriesLoading, setDataEntriesLoading] = useState(false);
    const [showDataEntryModal, setShowDataEntryModal] = useState(false);
    const [editingDataEntry, setEditingDataEntry] = useState(null);
    const [pendingChanges, setPendingChanges] = useState({
        creates: [],
        updates: [],
        deletes: []
    });

    // Data Processing Fields (Output Settings)
    const [dataProcessingFields, setDataProcessingFields] = useState([]);
    const [originalDataProcessingFields, setOriginalDataProcessingFields] = useState([]);
    const [dataProcessingFieldsLoading, setDataProcessingFieldsLoading] = useState(false);
    const [showDataProcessingFieldModal, setShowDataProcessingFieldModal] = useState(false);
    const [editingDataProcessingField, setEditingDataProcessingField] = useState(null);
    const [pendingProcessingChanges, setPendingProcessingChanges] = useState({
        creates: [],
        updates: [],
        deletes: []
    });
    const [dataProcessingFieldForm, setDataProcessingFieldForm] = useState({
        visible_string: '',
        column_name: '',
        schema_version: 1
    });

    // Runtime environment debug info (read-only)
    const runtimeInfo = useMemo(() => {
        const apiUri = getApiBaseForOAuthAndRedirects();
        const rootDomain = getDomain() || (typeof window !== 'undefined' ? window.location.host : '');
        const rootPath = getRootPath() || '';
        return { apiUri, rootDomain, rootPath };
    }, []);
    // Embedding model selection state
    const [embeddingModels, setEmbeddingModels] = useState([]); // [{id,name,dimensions,provider}]
    const [embeddingModelsLoading, setEmbeddingModelsLoading] = useState(false);
    const [embeddingModelQuery, setEmbeddingModelQuery] = useState('');
    const [showEmbeddingModelList, setShowEmbeddingModelList] = useState(false);
    const [embeddingModelHighlight, setEmbeddingModelHighlight] = useState(0);
    const embeddingModelInputRef = useRef(null);
    const embeddingModelListRef = useRef(null);
    const [pendingEmbeddingModel, setPendingEmbeddingModel] = useState(null);
    // Gate: require full-screen acknowledgement before first edit of embedding config (memory-only per page load)
    const [showEmbeddingEditGate, setShowEmbeddingEditGate] = useState(false);
    const embeddingEditGateAcknowledgedRef = useRef(false);
    const [showCreateProcessingTableConfirm, setShowCreateProcessingTableConfirm] = useState(false);
    const [showCreateEntryTableConfirm, setShowCreateEntryTableConfirm] = useState(false);
    const [showActiveModelPicker, setShowActiveModelPicker] = useState(false);
    const [showActiveWebSearchPicker, setShowActiveWebSearchPicker] = useState(false);
    const [showActiveModelProviderWarning, setShowActiveModelProviderWarning] = useState(false);
    const [pendingActiveModel, setPendingActiveModel] = useState(null);
    const [draftActiveModelProvider, setDraftActiveModelProvider] = useState('');
    const [draftActiveModelId, setDraftActiveModelId] = useState('');
    const [draftProviderModels, setDraftProviderModels] = useState([]);
    const [draftProviderModelsLoading, setDraftProviderModelsLoading] = useState(false);
    const [draftProviderModelsError, setDraftProviderModelsError] = useState('');
    const [draftActiveWebSearchProvider, setDraftActiveWebSearchProvider] = useState('');

    // Ingestion Groups (new tab)
    // Removed unused Supabase/ingestion group fields per linter

    // const resetIngestionForm = useCallback(() => {}, []);

    // const supabaseHeaders = useCallback(() => ({
    //     'apikey': supabaseServiceKey || '',
    //     'Authorization': `Bearer ${supabaseServiceKey || ''}`,
    //     'Content-Type': 'application/json'
    // }), [supabaseServiceKey]);

    // Ingestion Groups UI moved to Main page

    /* const handleCreateOrUpdateGroup = useCallback(async () => {
        if (!ingSuffix) {
            setMessage({ type: 'error', text: 'Active suffix is required.' });
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
            return;
        }
        let config;
        try {
            config = ingestionForm.configText ? JSON.parse(ingestionForm.configText) : {};
        } catch (e) {
            setMessage({ type: 'error', text: 'Config JSON is invalid.' });
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
            return;
        }
        if (!ingestionForm.group_slug || !ingestionForm.name) {
            setMessage({ type: 'error', text: 'Group slug and name are required.' });
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
            return;
        }
        try {
            setSaving(true);
            const base = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(ingSuffix)}/groups`;
            // Build payload per mode: do not send groupSlug on PATCH (slug is immutable)
            const payloadCreate = { groupSlug: ingestionForm.group_slug, name: ingestionForm.name, config };
            const payloadEdit = { name: ingestionForm.name, config };
            let resp;
            const headers = await getAuthHeaders();
            if (ingestionForm.mode === 'create') {
                resp = await fetch(base, { method: 'POST', headers, body: JSON.stringify(payloadCreate) });
            } else {
                const url = `${base}/${encodeURIComponent(ingestionForm.group_slug)}`;
                resp = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify(payloadEdit) });
            }
            const json = await resp.json().catch(() => ({}));
            if (!resp.ok || json?.success === false) {
                throw new Error(json?.message || json?.error || 'Failed to save group');
            }
            setMessage({ type: 'success', text: ingestionForm.mode === 'create' ? 'Group created.' : 'Group updated.' });
            setTimeout(() => setMessage({type: '', text: ''}), 4000);
            await refreshGroups();
            if (ingestionForm.mode === 'create') resetIngestionForm();
        } catch (e) {
            setMessage({ type: 'error', text: e.message || 'Failed to save group' });
            setTimeout(() => setMessage({type: '', text: ''}), 7000);
        } finally {
            setSaving(false);
        }
    }, [ingSuffix, ingestionForm, refreshGroups, resetIngestionForm]); */

    /* const handleDeleteGroup = useCallback(async (slug) => {
        if (!ingSuffix) {
            setMessage({ type: 'error', text: 'Active suffix is required.' });
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
            return;
        }
        if (!window.confirm(`Delete group ${slug}? This cannot be undone.`)) return;
        try {
            setSaving(true);
            await deleteIngestionGroupCascade(ingSuffix, slug);
            setMessage({ type: 'success', text: 'Group deleted.' });
            setTimeout(() => setMessage({type: '', text: ''}), 4000);
            await refreshGroups();
            // If the deleted group was the current selection, clear it.
            // GroupContext will select the first available group on next refresh.
            if (currentGroup === slug) setCurrentGroup('');
        } catch (e) {
            setMessage({ type: 'error', text: e.message || 'Failed to delete group' });
            setTimeout(() => setMessage({type: '', text: ''}), 7000);
        } finally {
            setSaving(false);
        }
    }, [ingSuffix, refreshGroups, currentGroup, setCurrentGroup]); */

    /* const controllerAction = useCallback(async (slug, action) => {
        try {
            setSaving(true);
            const url = `${API_BASE_URL}/casc/v1/api/suffixes/${encodeURIComponent(ingSuffix)}/groups/${encodeURIComponent(slug)}/controller/${action}`;
            const method = 'POST';
            const resp = await fetch(url, { method });
            const json = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(json?.error || 'Controller action failed');
            setMessage({ type: 'success', text: `${action} OK` });
            setTimeout(() => setMessage({type: '', text: ''}), 4000);
        } catch (e) {
            setMessage({ type: 'error', text: e.message || `Failed to ${action}` });
            setTimeout(() => setMessage({type: '', text: ''}), 7000);
        } finally {
            setSaving(false);
        }
    }, [ingSuffix]); */

    // Data Settings (suffix/instance management)
    const [dataSettings, setDataSettings] = useState({
        instances: [],
        activeSuffix: 'rsmg_sales'
    });
    const [originalDataSettings, setOriginalDataSettings] = useState({
        activeSuffix: 'rsmg_sales'
    });

    const dataEntriesLoadedRef = useRef(false);
    const dataEntriesLoadingRef = useRef(false);
    const settingsLoadedRef = useRef(false);
    const settingsLoadingRef = useRef(false);
    const [dataEntryForm, setDataEntryForm] = useState({
        visible_string: '',
        column_name: '',
        schema_version: 1
    });

    // Define load functions before useEffect hooks that use them
    const loadUserSettings = useCallback(async (providersList = null) => {
        if (!user?.email) return;

        const providerRows = Array.isArray(providersList) && providersList.length > 0
            ? providersList
            : providers;

        setUserSettingsLoading(true);
        try {
            const loadedSettings = {
                openaiKey: '',
                geminiKey: '',
                anthropicKey: '',
                llm_keys: {},
                web_search_keys: {},
                feelLuckyMcpServerId: '',
                activeModelId: '',
                activeModelProvider: '',
                activeWebSearchProvider: '',
                researchTimeoutMinutes: RESEARCH_TIMEOUT_DEFAULT_MINUTES,
                chatToolResultMaxFieldLength: CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT,
            };

            const response = await fetch(`${API_BASE_URL}/casc/v1/api/user/settings`, {
                method: 'POST',
                headers: await getAuthHeaders(),
                body: JSON.stringify({action: 'get', email: user.email}),
            });

            if (response.ok) {
                const data = await response.json();
                const apiSettings = data.settings || {};
                // Normalize llm_keys
                let llmKeys = apiSettings.llm_keys || {};
                // Backfill from legacy fields for supported providers
                try {
                    const mapping = [
                        {provider: 'openai', legacy: 'openaiKey'},
                        {provider: 'anthropic', legacy: 'anthropicKey'},
                        {provider: 'gemini', legacy: 'geminiKey'},
                    ];
                    const supported = (Array.isArray(providerRows) ? providerRows : [])
                        .map((item) => (typeof item === 'string' ? item : String(item?.slug || item?.id || '')).toLowerCase());
                    mapping.forEach(({provider, legacy}) => {
                        if (supported.includes(provider)) {
                            const keyName = `${provider}`.toUpperCase() + '_KEY';
                            if (!llmKeys[keyName] && apiSettings[legacy]) {
                                llmKeys[keyName] = apiSettings[legacy];
                            }
                        }
                    });
                } catch (e) {
                    console.warn('Backfill llm_keys from legacy failed:', e.message);
                }
                loadedSettings.openaiKey = apiSettings.openaiKey || '';
                loadedSettings.geminiKey = apiSettings.geminiKey || '';
                loadedSettings.anthropicKey = apiSettings.anthropicKey || '';
                loadedSettings.llm_keys = llmKeys;
                loadedSettings.web_search_keys = apiSettings.web_search_keys || {};
                loadedSettings.feelLuckyMcpServerId = apiSettings.feelLuckyMcpServerId || '';
                loadedSettings.pansynapseUserId = apiSettings.pansynapseUserId || '';
                loadedSettings.activeModelId = apiSettings.activeModelId || '';
                loadedSettings.activeModelProvider = String(apiSettings.activeModelProvider || '').toLowerCase();
                loadedSettings.activeWebSearchProvider = String(apiSettings.activeWebSearchProvider || '').toLowerCase();
                const timeoutMins = Number(apiSettings.researchTimeoutMinutes);
                loadedSettings.researchTimeoutMinutes = Number.isFinite(timeoutMins) && timeoutMins > 0
                    ? Math.min(RESEARCH_TIMEOUT_MAX_MINUTES, Math.max(RESEARCH_TIMEOUT_MIN_MINUTES, Math.floor(timeoutMins)))
                    : RESEARCH_TIMEOUT_DEFAULT_MINUTES;
                const fieldLen = Number(apiSettings.chatToolResultMaxFieldLength);
                loadedSettings.chatToolResultMaxFieldLength = Number.isFinite(fieldLen) && fieldLen > 0
                    ? Math.min(
                        CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_MAX,
                        Math.max(CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_MIN, Math.floor(fieldLen))
                    )
                    : CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT;
            }

            let providerKeyApiReady = false;
            try {
                const keysResponse = await listUserAiProviderKeys({ includeDisabledProviders: false });
                if (keysResponse?.success && Array.isArray(keysResponse.keys)) {
                    const byProviderId = new Map();
                    const byProviderSlug = new Map();

                    keysResponse.keys.forEach((item) => {
                        if (item?.providerId) byProviderId.set(item.providerId, item);
                        const slug = String(item?.provider?.slug || '').toLowerCase();
                        if (slug) byProviderSlug.set(slug, item);
                    });

                    const mappedLlmKeys = {};
                    if (Array.isArray(providerRows) && providerRows.length > 0) {
                        providerRows.forEach((provider) => {
                            const providerId = typeof provider === 'string'
                                ? provider
                                : String(provider?.id || provider?.slug || '');
                            const providerSlug = (typeof provider === 'string'
                                ? provider
                                : String(provider?.slug || provider?.id || '')).toLowerCase();
                            const keyName = `${providerSlug}`.toUpperCase() + '_KEY';
                            const record = byProviderId.get(providerId) || byProviderSlug.get(providerSlug);
                            mappedLlmKeys[keyName] = record?.maskedKey || '';
                        });
                    } else {
                        keysResponse.keys.forEach((item) => {
                            const providerSlug = String(item?.provider?.slug || '').toLowerCase();
                            if (!providerSlug) return;
                            mappedLlmKeys[`${providerSlug}`.toUpperCase() + '_KEY'] = item?.maskedKey || '';
                        });
                    }

                    loadedSettings.llm_keys = mappedLlmKeys;
                    loadedSettings.openaiKey = mappedLlmKeys.OPENAI_KEY || loadedSettings.openaiKey;
                    loadedSettings.geminiKey = mappedLlmKeys.GEMINI_KEY || mappedLlmKeys.GOOGLE_KEY || loadedSettings.geminiKey;
                    loadedSettings.anthropicKey = mappedLlmKeys.ANTHROPIC_KEY || loadedSettings.anthropicKey;
                    providerKeyApiReady = true;
                }
            } catch (e) {
                console.warn('Provider key API unavailable, using legacy key settings:', e.message || e);
            }

            setUserProviderKeysApiAvailable(providerKeyApiReady);
            setUserSettings(loadedSettings);
            setOriginalUserSettings(loadedSettings);
        } catch (error) {
            console.error('Error loading user settings:', error);
        } finally {
            setUserSettingsLoading(false);
        }
    }, [user?.email, providers]);

    const loadSystemSettings = useCallback(async () => {
        setSystemSettingsLoading(true);
        try {
            // Load current embedding model setting
            let embeddingModel = '';
            let aiRuntime = 'aisdk';
            let aiRuntimeHealth = null;
            let aiRuntimeStatus = 'unknown';
            let aiProviders = [];
            let aiProviderModels = [];
            try {
                const embHeaders = await getAuthHeaders();
                const embGetResp = await fetch(`${API_BASE_URL}/casc/v1/api/system-settings/get/EMBEDDING_MODEL`, {
                    method: 'POST',
                    headers: embHeaders,
                    body: JSON.stringify({})
                });
                if (embGetResp.ok) {
                    const embGetData = await embGetResp.json();
                    embeddingModel = embGetData?.value || '';
                }
            } catch (e) {
                console.warn('Failed to load EMBEDDING_MODEL:', e);
            }

            try {
                const runtime = await fetchAiRuntimeStatus();
                aiRuntime = runtime.runtime;
                aiRuntimeHealth = runtime.healthy;
                aiRuntimeStatus = runtime.status;
            } catch (e) {
                console.warn('Failed to load AI runtime status:', e);
                aiRuntimeStatus = 'unavailable';
            }

            try {
                const providersRes = await listAiProviders();
                if (providersRes?.success && Array.isArray(providersRes.providers)) {
                    aiProviders = providersRes.providers;
                }
            } catch (e) {
                console.warn('Failed to load AI providers:', e.message || e);
            }

            try {
                const modelsRes = await listAiProviderModels({ enabledOnly: false });
                if (modelsRes?.success && Array.isArray(modelsRes.models)) {
                    aiProviderModels = modelsRes.models;
                }
            } catch (e) {
                console.warn('Failed to load AI provider models:', e.message || e);
            }

            setSystemSettings({
                embeddingModel,
                aiRuntime,
                aiRuntimeHealth,
                aiRuntimeStatus,
                aiProviders,
                aiProviderModels
            });
            setOriginalSystemSettings({
                embeddingModel,
                aiRuntime,
                aiRuntimeHealth,
                aiRuntimeStatus,
                aiProviders,
                aiProviderModels
            });

            checkPersonalGoogleAuthStatus();
            loadUserGoogleCredentials();
            loadUserGoogleCredentialImports();
        } catch (error) {
            console.error('Error loading system settings:', error);
            setMessage({type: 'error', text: 'Error loading system settings'});
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
        } finally {
            setSystemSettingsLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load embedding models list
    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setEmbeddingModelsLoading(true);
            try {
                const data = await listAiCatalogModels({ embeddingOnly: true, limit: 2000 });
                if (mounted && data?.success && Array.isArray(data.models)) {
                    setEmbeddingModels(data.models.map((m) => ({
                        id: m.id,
                        name: m.name || m.id,
                        provider: m.providerName || m.providerId || m.providerApiType || 'unknown',
                        dimensions: m.embeddingDimensions || null,
                    })));
                }
            } catch (e) {
                console.error('Error loading embedding models:', e);
            } finally {
                if (mounted) setEmbeddingModelsLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, []);

    // Close embedding model dropdown when clicking outside
    useEffect(() => {
        const onDocClick = (e) => {
            const inputEl = embeddingModelInputRef.current;
            const listEl = embeddingModelListRef.current;
            if (!inputEl || !listEl) return;
            if (!inputEl.contains(e.target) && !listEl.contains(e.target)) {
                setShowEmbeddingModelList(false);
            }
        };
        if (showEmbeddingModelList) {
            document.addEventListener('mousedown', onDocClick);
        }
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [showEmbeddingModelList]);

    // Filtered list for embedding model combobox
    const filteredEmbeddingModels = useMemo(() => {
        const q = (embeddingModelQuery || '').trim().toLowerCase();
        if (!q) return embeddingModels || [];
        return (embeddingModels || []).filter(m => (m.name || m.id || '').toLowerCase().includes(q));
    }, [embeddingModels, embeddingModelQuery]);

    // Helper: display label for a model id
    const getEmbeddingModelLabel = useCallback((modelId) => {
        if (!modelId) return '';
        const m = (embeddingModels || []).find(x => x.id === modelId);
        return m ? (m.name || m.id) : modelId;
    }, [embeddingModels]);

    const handleSelectEmbeddingModel = (model) => {
        if (!model) return;
        // Populate the input immediately with the selected value
        setEmbeddingModelQuery(model.name || model.id);
        setPendingEmbeddingModel(model.id);
        setShowEmbeddingModelList(false);
        // Apply locally (stage only). Saving is deferred to global Save button.
        setTimeout(() => { applyEmbeddingModelChange(model.id); }, 0);
    };

    const applyEmbeddingModelChange = async (overrideModelId = null) => {
        const modelId = overrideModelId || pendingEmbeddingModel;
        if (!modelId) { return; }
        try {
            // Apply locally only; defer saving to the global Save button
            setSystemSettings(prev => ({ ...prev, embeddingModel: modelId }));
            // Keep the input populated with the selected label
            setEmbeddingModelQuery(getEmbeddingModelLabel(modelId));
            // Indicate unsaved change without calling backend
            setMessage({ type: 'info', text: 'Embedding model changed (not saved yet). Click "Save System Settings" to apply.' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        } catch (e) {
            console.error('Error applying embedding model locally:', e);
            setMessage({ type: 'error', text: e.message || 'Error applying embedding model locally' });
            setTimeout(() => setMessage({ type: '', text: '' }), 6000);
        } finally {
            setPendingEmbeddingModel(null);
        }
    };

    // Note: Previously had a cancelEmbeddingModelChange callback here, but it was unused.
    // If a UI control to cancel embedding model edits is added later, reintroduce it as needed.

    // Sync the visible input text when the saved setting or models list changes
    useEffect(() => {
        // Only auto-populate if the dropdown is not open (to avoid fighting user input)
        if (!showEmbeddingModelList) {
            setEmbeddingModelQuery(prev => {
                const desired = getEmbeddingModelLabel(systemSettings.embeddingModel);
                // If prev already matches desired, keep it; otherwise update
                return prev === desired ? prev : desired;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [systemSettings.embeddingModel, embeddingModels]);

    const loadDataProcessingFields = useCallback(async () => {
        setDataProcessingFieldsLoading(true);
        try {
            console.log('[SettingsPage] Loading data processing fields...');
            const response = await fetch(`${API_BASE_URL}/casc/v1/api/data-processing-fields`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({}),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.dataProcessingFields) {
                    setDataProcessingFields(data.dataProcessingFields);
                    setOriginalDataProcessingFields(JSON.parse(JSON.stringify(data.dataProcessingFields))); // Deep copy
                    setPendingProcessingChanges({creates: [], updates: [], deletes: []}); // Reset pending changes
                    console.log('[SettingsPage] Loaded', data.dataProcessingFields.length, 'data processing fields');
                }
            }
        } catch (error) {
            console.error('[SettingsPage] Error loading data processing fields:', error);
        } finally {
            setDataProcessingFieldsLoading(false);
        }
    }, []);

    useEffect(() => {
        // Don't load settings when user is not authenticated (e.g. during logout)
        if (!user) return;
        // Prevent duplicate API calls (e.g., from React StrictMode)
        if (settingsLoadedRef.current || settingsLoadingRef.current) {
            return;
        }
        settingsLoadedRef.current = true;
        settingsLoadingRef.current = true;

        const loadProviders = async () => {
            try {
                const registry = await listAiProviders();
                if (registry?.success && Array.isArray(registry.providers) && registry.providers.length > 0) {
                    return registry.providers.map((provider) => ({
                        ...provider,
                        slug: String(provider.slug || '').toLowerCase(),
                    }));
                }
            } catch (e) {
                console.warn('Failed to load provider registry, using user keys:', e.message || e);
            }

            try {
                const keysResponse = await listUserAiProviderKeys({ includeDisabledProviders: false });
                if (keysResponse?.success && Array.isArray(keysResponse.keys)) {
                    const deduped = new Map();
                    keysResponse.keys.forEach((item) => {
                        const provider = item?.provider;
                        if (!provider) return;
                        const slug = String(provider.slug || provider.id || '').toLowerCase();
                        if (!slug || deduped.has(slug)) return;
                        deduped.set(slug, {
                            ...provider,
                            id: provider.id || item.providerId || slug,
                            slug,
                            enabled: provider.enabled !== false,
                        });
                    });
                    const fromKeys = [...deduped.values()];
                    if (fromKeys.length > 0) return fromKeys;
                }
            } catch (e) {
                console.warn('Failed to derive providers from user keys:', e.message || e);
            }

            return [];
        };

        const loadAllSettings = async () => {
            try {
                const loadedProviders = await loadProviders();
                setProviders(loadedProviders);
                await Promise.all([
                    loadSystemSettings(),
                    loadUserSettings(loadedProviders),
                    loadDataSettings(),
                ]);
            } catch (error) {
                console.error('Error loading settings:', error);
                settingsLoadedRef.current = false; // Reset on error so it can retry
            } finally {
                settingsLoadingRef.current = false;
            }
        };

        loadAllSettings();

        // Prevent duplicate API calls for data entries (e.g., from React StrictMode or rapid tab switching)
        if (!dataEntriesLoadedRef.current && !dataEntriesLoadingRef.current) {
            dataEntriesLoadedRef.current = true;
            dataEntriesLoadingRef.current = true;
            loadDataEntries().finally(() => {
                dataEntriesLoadingRef.current = false;
            });
        }
    }, [user, loadSystemSettings, loadUserSettings]);

    // Load data processing fields when data-output tab is active
    useEffect(() => {
        if (activeTab === 'data-output' && dataProcessingFields.length === 0 && !dataProcessingFieldsLoading) {
            loadDataProcessingFields();
        }
    }, [activeTab, dataProcessingFields.length, dataProcessingFieldsLoading, loadDataProcessingFields]);

    const loadDataEntries = async () => {
        setDataEntriesLoading(true);
        try {
            console.log('[SettingsPage] Loading data entries...');
            const deHeaders = await getAuthHeaders();
            const response = await fetch(`${API_BASE_URL}/casc/v1/api/data-entries`, {
                method: 'POST',
                headers: deHeaders,
                body: JSON.stringify({}),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.dataEntries) {
                    setDataEntries(data.dataEntries);
                    setOriginalDataEntries(JSON.parse(JSON.stringify(data.dataEntries))); // Deep copy
                    setPendingChanges({creates: [], updates: [], deletes: []}); // Reset pending changes
                    console.log('[SettingsPage] Loaded', data.dataEntries.length, 'data entries');
                }
            }
        } catch (error) {
            console.error('[SettingsPage] Error loading data entries:', error);
            dataEntriesLoadedRef.current = false; // Reset on error so it can retry
        } finally {
            setDataEntriesLoading(false);
        }
    };

    const hasDataEntryChanges = () => {
        return pendingChanges.creates.length > 0 ||
            pendingChanges.updates.length > 0 ||
            pendingChanges.deletes.length > 0;
    };

    const handleAddDataEntry = () => {
        // Calculate next schema version from ORIGINAL entries only (not pending creates)
        // All new entries added in this session should use the same schema version
        const maxSchemaVersion = originalDataEntries.length > 0
            ? Math.max(...originalDataEntries.map(e => e.schema_version || 1))
            : 0;
        const nextSchemaVersion = maxSchemaVersion + 1;

        setDataEntryForm({
            visible_string: '',
            column_name: '',
            schema_version: nextSchemaVersion,
        });
        setEditingDataEntry(null);
        setShowDataEntryModal(true);
    };

    const handleUpdateDatabaseClick = () => {
        if (!hasDataEntryChanges()) {
            setMessage({type: 'info', text: 'No changes to commit'});
            setTimeout(() => setMessage({type: '', text: ''}), 3000);
            return;
        }
        setShowUpdateConfirm(true);
    };

    const handleUpdateDatabase = async () => {
        setShowUpdateConfirm(false);

        setSaving(true);
        try {
            const errors = [];

            // Check if there are any schema changes (field changes) in the pending updates
            let hasSchemaChanges = false;
            for (const updateEntry of pendingChanges.updates) {
                if (!pendingChanges.deletes.includes(updateEntry.id)) {
                    const originalEntry = originalDataEntries.find(e => e.id === updateEntry.id);
                    if (originalEntry) {
                        // Check if column_name or visible_string changed (schema change)
                        const schemaChanged = originalEntry
                            ? (originalEntry.column_name !== updateEntry.column_name ||
                                originalEntry.visible_string !== updateEntry.visible_string)
                            : false;
                        if (schemaChanged) {
                            hasSchemaChanges = true;
                            break;
                        }
                    }
                }
            }

            // Also check if there are any new creates (they will have schema changes by definition)
            if (pendingChanges.creates.length > 0) {
                hasSchemaChanges = true;
            }

            // Calculate max schema version from original entries in DB (what was read initially)
            // This is the baseline - we increment from this
            // If no entries exist, baseline is 1 (first save)
            const maxOriginalSchemaVersion = originalDataEntries.length > 0
                ? Math.max(...originalDataEntries.map(e => e.schema_version || 1))
                : 1;

            // If there are schema changes, increment the version from the baseline
            // Otherwise, keep the existing versions (shouldn't happen if we're committing, but handle it)
            const newSchemaVersion = hasSchemaChanges ? (maxOriginalSchemaVersion + 1) : maxOriginalSchemaVersion;

            // Process deletes first
            for (const entryId of pendingChanges.deletes) {
                try {
                    const delHeaders = await getAuthHeaders();
                    const response = await fetch(`${API_BASE_URL}/casc/v1/api/data-entries/delete/${entryId}`, {
                        method: 'POST',
                        headers: delHeaders,
                    });

                    if (!response.ok) {
                        const data = await response.json();
                        errors.push(`Failed to delete ${entryId}: ${data.error || 'Unknown error'}`);
                    }
                } catch (error) {
                    errors.push(`Error deleting ${entryId}: ${error.message}`);
                }
            }

            // Process creates - all new entries get the new schema version if there are schema changes
            // If no schema changes (shouldn't happen with creates, but handle it), use version 1
            for (const createEntry of pendingChanges.creates) {
                try {
                    const {tempId, ...entryData} = createEntry;
                    const createHeaders = await getAuthHeaders();
                    const response = await fetch(`${API_BASE_URL}/casc/v1/api/data-entries/create`, {
                        method: 'POST',
                        headers: createHeaders,
                        body: JSON.stringify({
                            ...entryData,
                            schema_version: hasSchemaChanges ? newSchemaVersion : 1
                        }),
                    });

                    if (!response.ok) {
                        const data = await response.json();
                        errors.push(`Failed to create ${entryData.visible_string}: ${data.error || 'Unknown error'}`);
                    }
                } catch (error) {
                    errors.push(`Error creating entry: ${error.message}`);
                }
            }

            // Process updates - only increment schema version if schema changed
            for (const updateEntry of pendingChanges.updates) {
                try {
                    if (pendingChanges.deletes.includes(updateEntry.id)) {
                        continue; // Skip if this entry is being deleted
                    }

                    const originalEntry = originalDataEntries.find(e => e.id === updateEntry.id);
                    // Check if column_name or visible_string changed (schema change)
                    const schemaChanged = originalEntry
                        ? (originalEntry.column_name !== updateEntry.column_name ||
                            originalEntry.visible_string !== updateEntry.visible_string)
                        : false;

                    // If schema changed, use new schema version; otherwise keep original version
                    const schemaVersionToUse = schemaChanged
                        ? newSchemaVersion
                        : (originalEntry?.schema_version || 1);

                    const updateHeaders = await getAuthHeaders();
                    const response = await fetch(`${API_BASE_URL}/casc/v1/api/data-entries/update/${updateEntry.id}`, {
                        method: 'POST',
                        headers: updateHeaders,
                        body: JSON.stringify({
                            ...updateEntry,
                            schema_version: schemaVersionToUse
                        }),
                    });

                    if (!response.ok) {
                        const data = await response.json();
                        errors.push(`Failed to update ${updateEntry.visible_string}: ${data.error || 'Unknown error'}`);
                    }
                } catch (error) {
                    errors.push(`Error updating entry: ${error.message}`);
                }
            }

            if (errors.length > 0) {
                setMessage({type: 'error', text: `Some operations failed: ${errors.join('; ')}`});
                setTimeout(() => setMessage({type: '', text: ''}), 10000);
            } else {
                setMessage({type: 'success', text: 'All changes committed successfully to database'});
                setTimeout(() => setMessage({type: '', text: ''}), 3000);
                // Reload data entries to get fresh data from database
                await loadDataEntries();

                // If there were schema changes, show confirmation to create entry table
                if (hasSchemaChanges) {
                    // Store the new schema version for use when creating the table
                    setShowCreateEntryTableConfirm(true);
                    // Store schema version in a ref or state for the create table action
                    window._pendingEntrySchemaVersion = newSchemaVersion;
                }
            }
        } catch (error) {
            console.error('Error updating database:', error);
            setMessage({type: 'error', text: 'Error updating database'});
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
        } finally {
            setSaving(false);
        }
    };

    const handleDiscardChangesClick = () => {
        setShowDiscardConfirm(true);
    };

    const handleDiscardChanges = () => {
        setShowDiscardConfirm(false);
        // Reload from database to reset local state
        loadDataEntries();
        setMessage({type: 'info', text: 'All pending changes discarded'});
        setTimeout(() => setMessage({type: '', text: ''}), 3000);
    };

    const handleEditDataEntry = (entry) => {
        setDataEntryForm({
            visible_string: entry.visible_string || '',
            column_name: entry.column_name || '',
            schema_version: entry.schema_version || 1
        });
        setEditingDataEntry(entry);
        setShowDataEntryModal(true);
    };

    const handleDeleteDataEntryClick = (entryId) => {
        setEntryToDelete(entryId);
        setShowDeleteConfirm(true);
    };

    const handleDeleteDataEntry = () => {
        if (!entryToDelete) {
            return;
        }
        const entryId = entryToDelete;
        setEntryToDelete(null);
        setShowDeleteConfirm(false);

        // Check if this entry is in pending creates (if so, just remove it from creates)
        const createIndex = pendingChanges.creates.findIndex(c => c.tempId === entryId);
        if (createIndex !== -1) {
            // Remove from pending creates
            setPendingChanges(prev => ({
                ...prev,
                creates: prev.creates.filter((_, i) => i !== createIndex)
            }));
            // Also remove from local dataEntries
            setDataEntries(prev => prev.filter(e => e.tempId !== entryId));
            return;
        }

        // Check if this entry is in pending updates (if so, remove from updates and add to deletes)
        const updateIndex = pendingChanges.updates.findIndex(u => u.id === entryId);
        if (updateIndex !== -1) {
            // Remove from updates, add to deletes
            const entryToDeleteFromUpdates = pendingChanges.updates[updateIndex];
            setPendingChanges(prev => ({
                ...prev,
                updates: prev.updates.filter((_, i) => i !== updateIndex),
                deletes: [...prev.deletes, entryToDeleteFromUpdates.id]
            }));
        } else {
            // Add to pending deletes
            setPendingChanges(prev => ({
                ...prev,
                deletes: [...prev.deletes, entryId]
            }));
        }

        // Remove from local dataEntries (but keep in original for undo)
        setDataEntries(prev => prev.filter(e => e.id !== entryId));
    };

    const handleSaveDataEntry = () => {
        // Validate required fields
        if (!dataEntryForm.visible_string || !dataEntryForm.column_name) {
            setMessage({type: 'error', text: 'Please fill in all required fields (Visible String and Column Name)'});
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
            return;
        }

        // Use the schema version from the form (which should be the next version for new entries)
        const entryData = {
            visible_string: dataEntryForm.visible_string,
            column_name: dataEntryForm.column_name,
            schema_version: dataEntryForm.schema_version || 1
        };

        if (editingDataEntry) {
            // Check if this was a pending create (has tempId)
            if (editingDataEntry.tempId) {
                // Update the pending create
                const createIndex = pendingChanges.creates.findIndex(c => c.tempId === editingDataEntry.tempId);
                if (createIndex !== -1) {
                    setPendingChanges(prev => ({
                        ...prev,
                        creates: prev.creates.map((c, i) => i === createIndex ? {
                            ...entryData,
                            tempId: editingDataEntry.tempId
                        } : c)
                    }));
                    // Update local dataEntries
                    setDataEntries(prev => prev.map(e =>
                        e.tempId === editingDataEntry.tempId
                            ? {...entryData, tempId: editingDataEntry.tempId}
                            : e
                    ));
                }
            } else {
                // Update existing entry - add to pending updates
                setPendingChanges(prev => ({
                    ...prev,
                    updates: prev.updates.filter(u => u.id !== editingDataEntry.id).concat([{
                        id: editingDataEntry.id,
                        ...entryData
                    }])
                }));
                // Update local dataEntries
                setDataEntries(prev => prev.map(e =>
                    e.id === editingDataEntry.id
                        ? {...e, ...entryData}
                        : e
                ));
            }
        } else {
            // Create new entry - add to pending creates with tempId
            const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const newEntry = {...entryData, tempId};

            setPendingChanges(prev => ({
                ...prev,
                creates: [...prev.creates, newEntry]
            }));

            // Add to local dataEntries
            setDataEntries(prev => [...prev, newEntry]);
        }

        setShowDataEntryModal(false);
        setEditingDataEntry(null);
        setMessage({type: 'info', text: 'Changes saved locally. Click "Update Database" to commit all changes.'});
        setTimeout(() => setMessage({type: '', text: ''}), 3000);
    };

    // =============================================================================
    // DATA PROCESSING FIELDS FUNCTIONS (Output Settings)
    // =============================================================================

    const hasDataProcessingFieldChanges = () => {
        return pendingProcessingChanges.creates.length > 0 ||
            pendingProcessingChanges.updates.length > 0 ||
            pendingProcessingChanges.deletes.length > 0;
    };

    const handleAddDataProcessingField = () => {
        // Calculate next schema version from ORIGINAL fields only (not pending creates)
        // All new fields added in this session should use the same schema version
        const maxSchemaVersion = originalDataProcessingFields.length > 0
            ? Math.max(...originalDataProcessingFields.map(f => f.schema_version || 1))
            : 0;
        const nextSchemaVersion = maxSchemaVersion + 1;

        setDataProcessingFieldForm({
            visible_string: '',
            column_name: '',
            schema_version: nextSchemaVersion,
        });
        setEditingDataProcessingField(null);
        setShowDataProcessingFieldModal(true);
    };

    const handleEditDataProcessingField = (field) => {
        setDataProcessingFieldForm({
            visible_string: field.visible_string || '',
            column_name: field.column_name || '',
            schema_version: field.schema_version || 1
        });
        setEditingDataProcessingField(field);
        setShowDataProcessingFieldModal(true);
    };

    const handleDeleteDataProcessingFieldClick = (fieldId) => {
        // Similar to handleDeleteDataEntryClick
        const createIndex = pendingProcessingChanges.creates.findIndex(c => c.tempId === fieldId);
        if (createIndex !== -1) {
            setPendingProcessingChanges(prev => ({
                ...prev,
                creates: prev.creates.filter((_, i) => i !== createIndex)
            }));
            setDataProcessingFields(prev => prev.filter(f => f.tempId !== fieldId));
            return;
        }

        const updateIndex = pendingProcessingChanges.updates.findIndex(u => u.id === fieldId);
        if (updateIndex !== -1) {
            const fieldToDelete = pendingProcessingChanges.updates[updateIndex];
            setPendingProcessingChanges(prev => ({
                ...prev,
                updates: prev.updates.filter((_, i) => i !== updateIndex),
                deletes: [...prev.deletes, fieldToDelete.id]
            }));
        } else {
            setPendingProcessingChanges(prev => ({
                ...prev,
                deletes: [...prev.deletes, fieldId]
            }));
        }

        setDataProcessingFields(prev => prev.filter(f => f.id !== fieldId));
    };

    const handleSaveDataProcessingField = () => {
        if (!dataProcessingFieldForm.visible_string || !dataProcessingFieldForm.column_name) {
            setMessage({type: 'error', text: 'Please fill in all required fields (Visible String and Column Name)'});
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
            return;
        }

        const fieldData = {
            visible_string: dataProcessingFieldForm.visible_string,
            column_name: dataProcessingFieldForm.column_name,
            schema_version: dataProcessingFieldForm.schema_version || 1
        };

        if (editingDataProcessingField) {
            if (editingDataProcessingField.tempId) {
                const createIndex = pendingProcessingChanges.creates.findIndex(c => c.tempId === editingDataProcessingField.tempId);
                if (createIndex !== -1) {
                    setPendingProcessingChanges(prev => ({
                        ...prev,
                        creates: prev.creates.map((c, i) => i === createIndex ? {
                            ...fieldData,
                            tempId: editingDataProcessingField.tempId
                        } : c)
                    }));
                    setDataProcessingFields(prev => prev.map(f =>
                        f.tempId === editingDataProcessingField.tempId
                            ? {...fieldData, tempId: editingDataProcessingField.tempId}
                            : f
                    ));
                }
            } else {
                setPendingProcessingChanges(prev => ({
                    ...prev,
                    updates: prev.updates.filter(u => u.id !== editingDataProcessingField.id).concat([{
                        id: editingDataProcessingField.id,
                        ...fieldData
                    }])
                }));
                setDataProcessingFields(prev => prev.map(f =>
                    f.id === editingDataProcessingField.id
                        ? {...f, ...fieldData}
                        : f
                ));
            }
        } else {
            const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const newField = {...fieldData, tempId};

            setPendingProcessingChanges(prev => ({
                ...prev,
                creates: [...prev.creates, newField]
            }));

            setDataProcessingFields(prev => [...prev, newField]);
        }

        setShowDataProcessingFieldModal(false);
        setEditingDataProcessingField(null);
        setMessage({type: 'info', text: 'Changes saved locally. Click "Update Database" to commit all changes.'});
        setTimeout(() => setMessage({type: '', text: ''}), 3000);
    };

    const handleUpdateProcessingDatabase = async () => {
        setShowUpdateConfirm(false);

        setSaving(true);
        try {
            const errors = [];

            // Check for schema changes
            let hasSchemaChanges = false;
            for (const updateField of pendingProcessingChanges.updates) {
                if (!pendingProcessingChanges.deletes.includes(updateField.id)) {
                    const originalField = originalDataProcessingFields.find(f => f.id === updateField.id);
                    if (originalField) {
                        const schemaChanged = originalField.column_name !== updateField.column_name ||
                            originalField.visible_string !== updateField.visible_string;
                        if (schemaChanged) {
                            hasSchemaChanges = true;
                            break;
                        }
                    }
                }
            }

            if (pendingProcessingChanges.creates.length > 0) {
                hasSchemaChanges = true;
            }

            const maxOriginalSchemaVersion = originalDataProcessingFields.length > 0
                ? Math.max(...originalDataProcessingFields.map(f => f.schema_version || 1))
                : 1;

            const newSchemaVersion = hasSchemaChanges ? (maxOriginalSchemaVersion + 1) : maxOriginalSchemaVersion;

            // Process deletes
            for (const fieldId of pendingProcessingChanges.deletes) {
                try {
                    const headers = await getAuthHeaders();
                    const response = await fetch(`${API_BASE_URL}/casc/v1/api/data-processing-fields/delete/${fieldId}`, {
                        method: 'POST',
                        headers,
                    });

                    if (!response.ok) {
                        const data = await response.json();
                        errors.push(`Failed to delete field: ${data.error || 'Unknown error'}`);
                    }
                } catch (error) {
                    errors.push(`Error deleting field: ${error.message}`);
                }
            }

            // Process creates
            for (const createField of pendingProcessingChanges.creates) {
                try {
                    const {tempId, ...fieldData} = createField;
                    const headers = await getAuthHeaders();
                    const response = await fetch(`${API_BASE_URL}/casc/v1/api/data-processing-fields/create`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({
                            ...fieldData,
                            schema_version: hasSchemaChanges ? newSchemaVersion : 1
                        }),
                    });

                    if (!response.ok) {
                        const data = await response.json();
                        errors.push(`Failed to create ${fieldData.visible_string}: ${data.error || 'Unknown error'}`);
                    }
                } catch (error) {
                    errors.push(`Error creating field: ${error.message}`);
                }
            }

            // Process updates
            for (const updateField of pendingProcessingChanges.updates) {
                try {
                    if (pendingProcessingChanges.deletes.includes(updateField.id)) {
                        continue;
                    }

                    const originalField = originalDataProcessingFields.find(f => f.id === updateField.id);
                    const schemaChanged = originalField
                        ? (originalField.column_name !== updateField.column_name ||
                            originalField.visible_string !== updateField.visible_string)
                        : false;

                    const schemaVersionToUse = schemaChanged
                        ? newSchemaVersion
                        : (originalField?.schema_version || 1);

                    const headers = await getAuthHeaders();
                    const response = await fetch(`${API_BASE_URL}/casc/v1/api/data-processing-fields/update/${updateField.id}`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({
                            ...updateField,
                            schema_version: schemaVersionToUse
                        }),
                    });

                    if (!response.ok) {
                        const data = await response.json();
                        errors.push(`Failed to update ${updateField.visible_string}: ${data.error || 'Unknown error'}`);
                    }
                } catch (error) {
                    errors.push(`Error updating field: ${error.message}`);
                }
            }

            if (errors.length > 0) {
                setMessage({type: 'error', text: `Some operations failed: ${errors.join('; ')}`});
                setTimeout(() => setMessage({type: '', text: ''}), 10000);
            } else {
                setMessage({type: 'success', text: 'All changes committed successfully to database'});
                setTimeout(() => setMessage({type: '', text: ''}), 3000);
                await loadDataProcessingFields();

                // If there were schema changes, show confirmation to create processing table
                if (hasSchemaChanges) {
                    // Store the new schema version for use when creating the table
                    setShowCreateProcessingTableConfirm(true);
                    // Store schema version in a ref or state for the create table action
                    window._pendingProcessingSchemaVersion = newSchemaVersion;
                }
            }
        } catch (error) {
            console.error('Error updating processing database:', error);
            setMessage({type: 'error', text: 'Error updating database'});
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
        } finally {
            setSaving(false);
        }
    };

    const loadDataSettings = async () => {
        setDataSettingsLoading(true);
        try {
            // Load instances
            const instancesHeaders = await getAuthHeaders();
            const instancesResponse = await fetch(`${API_BASE_URL}/casc/v1/api/data-settings/instances`, {
                method: 'POST',
                headers: instancesHeaders,
                body: JSON.stringify({})
            });

            let instances = [];
            if (instancesResponse.ok) {
                const instancesData = await instancesResponse.json();
                instances = instancesData.instances || [];
            }

            // Load active suffix
            const activeSuffixHeaders = await getAuthHeaders();
            const activeSuffixResponse = await fetch(`${API_BASE_URL}/casc/v1/api/data-settings/active-suffix`, {
                method: 'POST',
                headers: activeSuffixHeaders,
                body: JSON.stringify({})
            });

            let activeSuffix = 'rsmg_sales';
            if (activeSuffixResponse.ok) {
                const activeSuffixData = await activeSuffixResponse.json();
                activeSuffix = activeSuffixData.currentSuffix || 'rsmg_sales';
            }

            setDataSettings({
                instances,
                activeSuffix
            });
            setOriginalDataSettings({
                activeSuffix
            });
        } catch (error) {
            console.error('Error loading data settings:', error);
            setMessage({type: 'error', text: 'Error loading data settings'});
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
        } finally {
            setDataSettingsLoading(false);
        }
    };

    // ── Personal Google OAuth credentials (per-user OAuth app) ─────

    const loadUserGoogleCredentials = useCallback(async () => {
        setUserGoogleCredentials(prev => ({ ...prev, loading: true }));
        try {
            const data = await getUserGoogleCredentials();
            if (data.success) {
                setUserGoogleCredentials({
                    client_id: data.credentials?.client_id || '',
                    client_secret: '',
                    redirect_uri: data.credentials?.redirect_uri || '',
                    hasCredentials: data.credentials?.hasCredentials || false,
                    loading: false,
                });
            }
        } catch (error) {
            console.error('Error loading user Google credentials:', error);
            setUserGoogleCredentials(prev => ({ ...prev, loading: false }));
        }
    }, []);

    const loadUserGoogleCredentialImports = useCallback(async () => {
        setUserGoogleCredentialImportsLoading(true);
        try {
            const data = await listUserGoogleCredentialImports();
            const providers = data.providers || [];
            setUserGoogleCredentialImports(providers);
            setSelectedGoogleCredentialImportId(prev => (
                providers.some(provider => provider.id === prev)
                    ? prev
                    : (providers[0]?.id || '')
            ));
        } catch (error) {
            console.error('Error loading importable Google credentials:', error);
            setUserGoogleCredentialImports([]);
            setSelectedGoogleCredentialImportId('');
        } finally {
            setUserGoogleCredentialImportsLoading(false);
        }
    }, []);

    const handleImportUserGoogleCredentials = async () => {
        if (!selectedGoogleCredentialImportId) {
            setMessage({ type: 'error', text: 'Select Google credentials to import.' });
            setTimeout(() => setMessage({ type: '', text: '' }), 4000);
            return;
        }

        setUserGoogleCredentialImporting(true);
        try {
            const data = await importUserGoogleCredentials(selectedGoogleCredentialImportId);
            if (data.success) {
                setMessage({ type: 'success', text: 'Google OAuth credentials imported.' });
                setTimeout(() => setMessage({ type: '', text: '' }), 4000);
                await loadUserGoogleCredentials();
                await checkPersonalGoogleAuthStatus();
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.message || 'Failed to import Google OAuth credentials.' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        } finally {
            setUserGoogleCredentialImporting(false);
        }
    };

    const handleSaveUserGoogleCredentials = async () => {
        if (!userGoogleCredentials.client_id) {
            setMessage({ type: 'error', text: 'Google Client ID is required.' });
            setTimeout(() => setMessage({ type: '', text: '' }), 4000);
            return;
        }
        const redirectUri = userGoogleCredentials.redirect_uri ||
            `${runtimeInfo.apiUri}/casc/v1/api/google/auth/callback`;
        setUserGoogleCredentialsSaving(true);
        try {
            const secretTrim = (userGoogleCredentials.client_secret || '').trim();
            const data = await saveUserGoogleCredentials({
                client_id: userGoogleCredentials.client_id,
                ...(secretTrim ? { client_secret: secretTrim } : {}),
                redirect_uri: redirectUri,
            });
            if (data.success) {
                setMessage({ type: 'success', text: 'Google OAuth credentials saved.' });
                setTimeout(() => setMessage({ type: '', text: '' }), 4000);
                await loadUserGoogleCredentials();
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.message || 'Failed to save Google OAuth credentials.' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        } finally {
            setUserGoogleCredentialsSaving(false);
        }
    };

    // ── Personal Google OAuth ──────────────────────────────────────

    const checkPersonalGoogleAuthStatus = async () => {
        setPersonalGoogleAuth(prev => ({...prev, loading: true}));
        try {
            const data = await getUserGoogleAuthStatus();
            if (data.success) {
                setPersonalGoogleAuth({
                    isConnected: data.personal?.isConnected || false,
                    connectedUser: data.personal?.connectedUser || null,
                    loading: false,
                    activeSource: data.activeSource || null,
                    systemConnected: data.system?.isConnected || false,
                    canAccessDrive: data.canAccessDrive || false,
                    driveAccessFailureReason: data.driveAccessFailureReason || null,
                });
            }
        } catch (error) {
            console.error('Error checking personal Google auth status:', error);
            setPersonalGoogleAuth(prev => ({...prev, loading: false}));
        }
    };

    const handleConnectPersonalGoogle = async () => {
        try {
            const data = await getUserGoogleAuthUrl();
            if (data.success && data.authUrl) {
                window.location.href = data.authUrl;
            } else {
                setMessage({type: 'error', text: data.error || 'Failed to generate personal OAuth URL'});
                setTimeout(() => setMessage({type: '', text: ''}), 5000);
            }
        } catch (error) {
            console.error('Error connecting personal Google:', error);
            setMessage({type: 'error', text: `Failed to connect personal Google: ${error.message}`});
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
        }
    };

    const handleDisconnectPersonalGoogle = () => {
        setShowPersonalDisconnectConfirm(true);
    };

    const confirmDisconnectPersonalGoogle = async () => {
        setShowPersonalDisconnectConfirm(false);
        setSaving(true);
        try {
            const data = await disconnectUserGoogleAuth();
            if (data.success) {
                setMessage({type: 'success', text: 'Disconnected personal Google account successfully'});
                setTimeout(() => setMessage({type: '', text: ''}), 3000);
                await checkPersonalGoogleAuthStatus();
                window.dispatchEvent(new Event('refreshStats'));
            }
        } catch (error) {
            console.error('Error disconnecting personal Google:', error);
            setMessage({type: 'error', text: `Failed to disconnect: ${error.message}`});
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
        } finally {
            setSaving(false);
        }
    };

    // Refresh auth status after OAuth callback
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const oauthSuccess = urlParams.get('oauth_success');
        const oauthError = urlParams.get('oauth_error');

        if (oauthSuccess === 'personal' || oauthSuccess === 'true') {
            window.history.replaceState({}, document.title, window.location.pathname);
            checkPersonalGoogleAuthStatus();
            setMessage({
                type: 'success',
                text: oauthSuccess === 'personal'
                    ? 'Personal Google account connected successfully!'
                    : 'Google account connected. Personal Google Drive is used for ingestion — see User Settings if something looks wrong.'
            });
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
        } else if (oauthError) {
            window.history.replaceState({}, document.title, window.location.pathname);
            checkPersonalGoogleAuthStatus();
            setMessage({type: 'error', text: 'OAuth error: ' + decodeURIComponent(oauthError)});
            setTimeout(() => setMessage({type: '', text: ''}), 8000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleUserSettingsChange = (field, value) => {
        // Allow editing - always update the value
        const updatedSettings = {...userSettings, [field]: value};
        setUserSettings(updatedSettings);
    };

    const getProviderSlug = useCallback((provider) => {
        const raw = typeof provider === 'string' ? provider : (provider?.slug || provider?.id || '');
        return String(raw || '').toLowerCase();
    }, []);
    const getProviderId = useCallback((provider) => {
        const raw = typeof provider === 'string' ? provider : (provider?.id || provider?.slug || '');
        return String(raw || '');
    }, []);
    const getProviderDisplayName = useCallback((provider) => {
        if (provider && typeof provider === 'object' && provider.name) return provider.name;
        const slug = getProviderSlug(provider);
        return slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : 'Provider';
    }, [getProviderSlug]);
    const providerToKeyName = useCallback(
        (provider) => `${getProviderSlug(provider)}`.toUpperCase() + '_KEY',
        [getProviderSlug]
    );
    const getProviderKeyNames = useCallback((provider) => {
        const slug = getProviderSlug(provider);
        return LLM_PROVIDER_KEY_ALIASES[slug] || [providerToKeyName(provider)];
    }, [getProviderSlug, providerToKeyName]);
    const getApiTypeLabel = (apiType) => {
        const normalized = String(apiType || '').trim().toLowerCase();
        if (normalized === 'openai') return 'OpenAI';
        if (normalized === 'anthropic') return 'Anthropic';
        if (normalized === 'google' || normalized === 'gemini') return 'Gemini';
        if (normalized === 'ollama') return 'Ollama';
        if (normalized === 'pansynapse') return 'Pansynapse';
        return apiType || 'Unknown';
    };
    const getApiKeyPlaceholder = () => {
        return 'API Key';
    };
    const handleTestApiKey = async (provider) => {
        const providerId = getProviderId(provider);
        if (!providerId) {
            setMessage({ type: 'error', text: 'Unable to test key: provider not found.' });
            return;
        }

        const keyName = providerToKeyName(provider);
        const apiKey = (userSettings.llm_keys || {})[keyName] || '';
        const normalizedKey = String(apiKey || '').trim();
        const rootPath = getRootPath() || '';

        try {
            const storageKey = `apiKeyTestDraft:${providerId}`;
            if (hasValue(normalizedKey) && !isMasked(normalizedKey)) {
                window.sessionStorage.setItem(storageKey, normalizedKey);
            } else {
                window.sessionStorage.removeItem(storageKey);
            }
        } catch (_) {
            // noop
        }

        window.location.assign(`${rootPath}/settings/key-test?providerId=${encodeURIComponent(providerId)}`);
    };
    const isMasked = (val) => typeof val === 'string' && (
        /^\*+[^*]{0,4}$/.test(val)
        || val === '***'
        || /^.{3}\*\*\*$/.test(val)
        || /^.{3}\.\.\.[^.]{4}$/.test(val)
    );
    const hasValue = (val) => typeof val === 'string' && val.trim().length > 0;
    const hasStoredKey = useCallback((val) => typeof val === 'string' && val.trim().length > 0, []);
    const isProviderDirty = (provider) => {
        const keyName = providerToKeyName(provider);
        const current = (userSettings.llm_keys || {})[keyName] || '';
        const original = (originalUserSettings.llm_keys || {})[keyName] || '';
        return current !== original;
    };
    const normalizeWebSearchSettingValue = (keyName, val) => {
        const provider = WEB_SEARCH_PROVIDERS.find((p) => p.keyName === keyName);
        if (provider?.toggleKey) {
            return isFeelLuckyEnabled(val) ? 'y' : 'n';
        }
        return typeof val === 'string' ? val : '';
    };
    const isWebSearchKeyDirty = (keyName) => {
        const current = normalizeWebSearchSettingValue(keyName, (userSettings.web_search_keys || {})[keyName]);
        const original = normalizeWebSearchSettingValue(keyName, (originalUserSettings.web_search_keys || {})[keyName]);
        return current !== original;
    };

    const mapProviderSlugToPlatform = (providerSlug) => {
        const slug = String(providerSlug || '').toLowerCase();
        const provider = (providers || []).find((item) => getProviderSlug(item) === slug);
        const apiType = String(provider?.api_type || provider?.apiType || '').toLowerCase();
        if (apiType === 'google' || apiType === 'gemini' || slug === 'google' || slug === 'gemini') return 'google';
        if (apiType === 'anthropic' || slug === 'anthropic') return 'anthropic';
        if (apiType === 'pansynapse' || slug === 'pansynapse') return 'pansynapse';
        if (apiType === 'ollama' || slug === 'ollama') return 'ollama';
        if (apiType === 'openai_compatible' || slug === 'openai_compatible') return 'openai_compatible';
        if (['xai', 'groq', 'mistral', 'togetherai', 'deepseek', 'openrouter'].includes(apiType)) {
            return 'openai_compatible';
        }
        if (apiType === 'openai' || slug === 'openai') return 'openai';
        return slug || 'openai';
    };

    const isProviderKeyConfigured = useCallback((provider) => {
        const keyNames = getProviderKeyNames(provider);
        return keyNames.some((keyName) => {
            const current = (userSettings.llm_keys || {})[keyName];
            const original = (originalUserSettings.llm_keys || {})[keyName];
            return hasStoredKey(current) || hasStoredKey(original);
        });
    }, [userSettings.llm_keys, originalUserSettings.llm_keys, hasStoredKey, getProviderKeyNames]);

    const isWebSearchProviderConfigured = useCallback((provider) => {
        const current = (userSettings.web_search_keys || {})[provider.keyName];
        const original = (originalUserSettings.web_search_keys || {})[provider.keyName];
        if (provider.toggleKey) {
            return isFeelLuckyEnabled(current) || isFeelLuckyEnabled(original);
        }
        return hasStoredKey(current) || hasStoredKey(original);
    }, [userSettings.web_search_keys, originalUserSettings.web_search_keys, hasStoredKey]);

    const configuredLlmProviders = useMemo(() => {
        const bySlug = new Map();

        const addProvider = (provider) => {
            const slug = getProviderSlug(provider);
            if (!slug || !isProviderKeyConfigured(provider)) return;
            bySlug.set(slug, typeof provider === 'object' && provider !== null
                ? { ...provider, slug }
                : { id: slug, slug, name: slug.charAt(0).toUpperCase() + slug.slice(1) });
        };

        (providers || []).forEach(addProvider);

        const ingestKeyName = (keyName, val) => {
            if (!hasStoredKey(val)) return;
            const slug = String(keyName || '').replace(/_KEY$/i, '').toLowerCase();
            if (!slug) return;
            if (bySlug.has(slug)) return;
            if (slug === 'google' && bySlug.has('gemini')) return;
            if (slug === 'gemini' && bySlug.has('google')) return;
            bySlug.set(slug, {
                id: slug,
                slug,
                name: slug.charAt(0).toUpperCase() + slug.slice(1),
            });
        };

        Object.entries(userSettings.llm_keys || {}).forEach(([keyName, val]) => ingestKeyName(keyName, val));
        Object.entries(originalUserSettings.llm_keys || {}).forEach(([keyName, val]) => ingestKeyName(keyName, val));

        const legacyHasKey = {
            openai: hasStoredKey(userSettings.openaiKey) || hasStoredKey(originalUserSettings.openaiKey),
            anthropic: hasStoredKey(userSettings.anthropicKey) || hasStoredKey(originalUserSettings.anthropicKey),
            gemini: hasStoredKey(userSettings.geminiKey) || hasStoredKey(originalUserSettings.geminiKey),
        };
        Object.entries(legacyHasKey).forEach(([slug, hasKey]) => {
            if (!hasKey || bySlug.has(slug)) return;
            bySlug.set(slug, {
                id: slug,
                slug,
                name: slug.charAt(0).toUpperCase() + slug.slice(1),
            });
        });

        return [...bySlug.values()].sort((a, b) =>
            getProviderDisplayName(a).localeCompare(getProviderDisplayName(b), undefined, { sensitivity: 'base' })
        );
    }, [
        providers,
        isProviderKeyConfigured,
        userSettings.llm_keys,
        originalUserSettings.llm_keys,
        userSettings.openaiKey,
        userSettings.anthropicKey,
        userSettings.geminiKey,
        originalUserSettings.openaiKey,
        originalUserSettings.anthropicKey,
        originalUserSettings.geminiKey,
        getProviderSlug,
        getProviderDisplayName,
        hasStoredKey,
    ]);

    const configuredWebSearchProviders = useMemo(
        () => WEB_SEARCH_PROVIDERS.filter((provider) => isWebSearchProviderConfigured(provider)),
        [isWebSearchProviderConfigured]
    );

    const modelBelongsToProvider = useCallback((modelRow, providerSlug) => {
        const slug = String(modelRow?.ai_providers?.slug || '').toLowerCase();
        const providerId = String(modelRow?.provider_id || modelRow?.ai_providers?.id || '').toLowerCase();
        const targetSlug = String(providerSlug || '').toLowerCase();
        const targetProvider = (providers || []).find((item) => getProviderSlug(item) === targetSlug);
        const targetId = targetProvider ? String(getProviderId(targetProvider)).toLowerCase() : targetSlug;
        const slugMatches = slug === targetSlug
            || (LLM_PROVIDER_SLUG_ALIASES[targetSlug] || []).includes(slug);
        return slugMatches || providerId === targetId;
    }, [providers, getProviderSlug, getProviderId]);

    const getActiveModelLabel = useCallback(() => {
        const providerSlug = String(userSettings.activeModelProvider || '').toLowerCase();
        const modelId = String(userSettings.activeModelId || '').trim();
        if (!providerSlug) return 'Not selected';

        const provider = (providers || []).find((item) => getProviderSlug(item) === providerSlug)
            || configuredLlmProviders.find((item) => getProviderSlug(item) === providerSlug);
        const providerName = getProviderDisplayName(provider || providerSlug);
        if (!modelId) return providerName;

        const catalogGroup = activeModelOptions.find((group) => group.providerSlug === providerSlug);
        const catalogModel = catalogGroup?.models?.find((model) => model.id === modelId);
        if (catalogModel?.name) {
            return `${providerName} · ${catalogModel.name}`;
        }

        const modelRow = (systemSettings.aiProviderModels || []).find(
            (row) => modelBelongsToProvider(row, providerSlug)
                && String(row?.model_id || row?.id || '').trim() === modelId
        );
        const modelName = modelRow?.display_name || modelId;
        return `${providerName} · ${modelName}`;
    }, [
        userSettings.activeModelProvider,
        userSettings.activeModelId,
        providers,
        configuredLlmProviders,
        activeModelOptions,
        systemSettings.aiProviderModels,
        modelBelongsToProvider,
        getProviderDisplayName,
        getProviderSlug,
    ]);

    const getActiveWebSearchLabel = useCallback(() => {
        const providerId = String(userSettings.activeWebSearchProvider || '').toLowerCase();
        if (!providerId) return 'Not selected';
        const provider = WEB_SEARCH_PROVIDERS.find((item) => item.id === providerId);
        return provider ? provider.name : providerId;
    }, [userSettings.activeWebSearchProvider]);

    const saveActiveUserSelections = async (overrides = {}) => {
        if (!user?.email) {
            throw new Error('User authentication required to save settings');
        }
        const next = {
            activeModelId: overrides.activeModelId !== undefined
                ? String(overrides.activeModelId || '').trim()
                : (userSettings.activeModelId || ''),
            activeModelProvider: overrides.activeModelProvider !== undefined
                ? String(overrides.activeModelProvider || '').trim().toLowerCase()
                : String(userSettings.activeModelProvider || '').toLowerCase(),
            activeWebSearchProvider: overrides.activeWebSearchProvider !== undefined
                ? String(overrides.activeWebSearchProvider || '').trim().toLowerCase()
                : String(userSettings.activeWebSearchProvider || '').toLowerCase(),
        };
        const headers = await getAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/casc/v1/api/user/settings`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                email: user.email,
                activeModelId: next.activeModelId,
                activeModelProvider: next.activeModelProvider,
                activeWebSearchProvider: next.activeWebSearchProvider,
                currentActivePlatform: mapProviderSlugToPlatform(next.activeModelProvider),
            }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data?.success === false) {
            throw new Error(data?.error || data?.message || 'Failed to save active model / web search settings');
        }
        const apiSettings = data.settings || {};
        const saved = {
            activeModelId: apiSettings.activeModelId || next.activeModelId || '',
            activeModelProvider: String(apiSettings.activeModelProvider || next.activeModelProvider || '').toLowerCase(),
            activeWebSearchProvider: String(apiSettings.activeWebSearchProvider || next.activeWebSearchProvider || '').toLowerCase(),
        };
        setUserSettings((prev) => ({
            ...prev,
            ...saved,
        }));
        setOriginalUserSettings((prev) => ({
            ...prev,
            ...saved,
        }));
        return saved;
    };

    const applyActiveModelSelection = async (providerSlug, modelId) => {
        const normalizedProvider = String(providerSlug || '').toLowerCase();
        const normalizedModelId = String(modelId || '').trim();
        const previous = {
            activeModelProvider: String(userSettings.activeModelProvider || '').toLowerCase(),
            activeModelId: String(userSettings.activeModelId || '').trim(),
        };
        setUserSettings((prev) => ({
            ...prev,
            activeModelProvider: normalizedProvider,
            activeModelId: normalizedModelId,
        }));
        setShowActiveModelPicker(false);
        setShowActiveModelProviderWarning(false);
        setPendingActiveModel(null);

        setSaving(true);
        try {
            await saveActiveUserSelections({
                activeModelProvider: normalizedProvider,
                activeModelId: normalizedModelId,
            });
            setMessage({ type: 'success', text: 'Active model saved.' });
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        } catch (error) {
            console.error('Error saving active model:', error);
            setUserSettings((prev) => ({
                ...prev,
                ...previous,
            }));
            setMessage({ type: 'error', text: error?.message || 'Failed to save active model' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        } finally {
            setSaving(false);
        }
    };
    const buildActiveModelProviderChoices = useCallback(() => (
        configuredLlmProviders
            .map((provider) => ({
                providerId: getProviderId(provider),
                providerSlug: getProviderSlug(provider),
                providerName: getProviderDisplayName(provider),
                models: [],
            }))
            .filter((group) => group.providerSlug)
            .sort((a, b) => String(a.providerName || a.providerSlug).localeCompare(
                String(b.providerName || b.providerSlug),
                undefined,
                { sensitivity: 'base' }
            ))
    ), [configuredLlmProviders, getProviderDisplayName, getProviderId, getProviderSlug]);

    const mergeProviderModelsIntoActiveOptions = useCallback((providerSlug, models, providerName) => {
        const slug = String(providerSlug || '').trim().toLowerCase();
        if (!slug) return;
        setActiveModelOptions((prev) => {
            const bySlug = new Map(
                (prev.length > 0 ? prev : buildActiveModelProviderChoices())
                    .map((group) => [group.providerSlug, { ...group, models: [...(group.models || [])] }])
            );
            const existing = bySlug.get(slug) || {
                providerSlug: slug,
                providerName: providerName || slug,
                models: [],
            };
            bySlug.set(slug, {
                ...existing,
                providerName: providerName || existing.providerName,
                models: Array.isArray(models) ? models : [],
            });
            return [...bySlug.values()].sort((a, b) => String(a.providerName || a.providerSlug).localeCompare(
                String(b.providerName || b.providerSlug),
                undefined,
                { sensitivity: 'base' }
            ));
        });
    }, [buildActiveModelProviderChoices]);

    const loadDraftProviderModels = useCallback(async (providerSlug, preferredModelId = '') => {
        const slug = String(providerSlug || '').toLowerCase().trim();
        const requestId = ++draftProviderModelsRequestRef.current;

        if (!slug) {
            setDraftProviderModels([]);
            setDraftProviderModelsError('');
            setDraftProviderModelsLoading(false);
            return [];
        }

        setDraftProviderModels([]);
        setDraftProviderModelsLoading(true);
        setDraftProviderModelsError('');
        setDraftActiveModelId('');

        try {
            const result = await listUserActiveModelOptionsForProvider(slug);
            if (requestId !== draftProviderModelsRequestRef.current) {
                return [];
            }

            const providerName = String(result?.provider?.name || slug).trim();
            const models = Array.isArray(result.models) ? result.models : [];
            mergeProviderModelsIntoActiveOptions(slug, models, providerName);
            setDraftProviderModels(models);

            const preferred = String(preferredModelId || '').trim();
            if (preferred && models.some((model) => model.id === preferred)) {
                setDraftActiveModelId(preferred);
            } else if (models.length > 0) {
                setDraftActiveModelId(models[0].id);
            } else {
                setDraftActiveModelId('');
                setDraftProviderModelsError('No models available for this provider.');
            }

            return models;
        } catch (error) {
            if (requestId !== draftProviderModelsRequestRef.current) {
                return [];
            }

            setDraftProviderModels([]);
            setDraftProviderModelsError(error?.message || 'Failed to load models for this provider.');
            setDraftActiveModelId('');
            return [];
        } finally {
            if (requestId === draftProviderModelsRequestRef.current) {
                setDraftProviderModelsLoading(false);
            }
        }
    }, [mergeProviderModelsIntoActiveOptions]);

    const handleDraftActiveModelProviderChange = useCallback(async (providerSlug) => {
        const slug = String(providerSlug || '').toLowerCase().trim();
        setDraftActiveModelProvider(slug);
        await loadDraftProviderModels(slug);
    }, [loadDraftProviderModels]);

    const handleOpenActiveModelPicker = () => {
        const currentProvider = String(
            userSettings.activeModelProvider || configuredLlmProviders[0]?.slug || ''
        ).toLowerCase();
        const currentModel = String(userSettings.activeModelId || '').trim();

        setActiveModelOptions(buildActiveModelProviderChoices());
        setDraftProviderModels([]);
        setDraftProviderModelsError('');
        setDraftProviderModelsLoading(false);
        setDraftActiveModelProvider(currentProvider);
        setDraftActiveModelId('');
        setShowActiveModelPicker(true);

        if (currentProvider) {
            loadDraftProviderModels(currentProvider, currentModel);
        }
    };

    const handleCloseActiveModelPicker = () => {
        draftProviderModelsRequestRef.current += 1;
        setShowActiveModelPicker(false);
        setDraftProviderModelsLoading(false);
        setDraftProviderModelsError('');
    };

    const handleOpenActiveWebSearchPicker = () => {
        setDraftActiveWebSearchProvider(userSettings.activeWebSearchProvider || configuredWebSearchProviders[0]?.id || '');
        setShowActiveWebSearchPicker(true);
    };

    const handleConfirmActiveModelSelection = () => {
        if (!draftActiveModelProvider) return;
        const currentProvider = String(userSettings.activeModelProvider || '').toLowerCase();
        const nextProvider = String(draftActiveModelProvider || '').toLowerCase();
        const nextModelId = String(draftActiveModelId || '').trim();
        if (!nextModelId) return;

        const selectedGroup = activeModelOptions.find((group) => group.providerSlug === nextProvider);
        const catalogModel = draftProviderModels.find((model) => model.id === nextModelId)
            || selectedGroup?.models?.find((model) => model.id === nextModelId);

        if (currentProvider && nextProvider && currentProvider !== nextProvider) {
            const provider = (providers || []).find((item) => getProviderSlug(item) === nextProvider)
                || configuredLlmProviders.find((item) => getProviderSlug(item) === nextProvider);
            setPendingActiveModel({
                providerSlug: nextProvider,
                modelId: nextModelId,
                providerName: getProviderDisplayName(provider || nextProvider),
                modelName: catalogModel?.name || nextModelId,
            });
            setShowActiveModelProviderWarning(true);
            return;
        }
        applyActiveModelSelection(nextProvider, nextModelId);
    };

    const handleConfirmActiveModelProviderWarning = () => {
        if (!pendingActiveModel) return;
        applyActiveModelSelection(pendingActiveModel.providerSlug, pendingActiveModel.modelId);
    };

    const handleCancelActiveModelProviderWarning = () => {
        setShowActiveModelProviderWarning(false);
        setPendingActiveModel(null);
    };

    const handleConfirmActiveWebSearchSelection = async () => {
        if (!draftActiveWebSearchProvider) return;
        const nextProvider = String(draftActiveWebSearchProvider).toLowerCase();
        const previous = String(userSettings.activeWebSearchProvider || '').toLowerCase();
        setUserSettings((prev) => ({
            ...prev,
            activeWebSearchProvider: nextProvider,
        }));
        setShowActiveWebSearchPicker(false);

        setSaving(true);
        try {
            await saveActiveUserSelections({ activeWebSearchProvider: nextProvider });
            setMessage({ type: 'success', text: 'Active web search saved.' });
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        } catch (error) {
            console.error('Error saving active web search:', error);
            setUserSettings((prev) => ({
                ...prev,
                activeWebSearchProvider: previous,
            }));
            setMessage({ type: 'error', text: error?.message || 'Failed to save active web search' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        } finally {
            setSaving(false);
        }
    };

    const isActiveModelDraftSelected = (providerSlug, modelId) =>
        String(draftActiveModelProvider || '').toLowerCase() === String(providerSlug || '').toLowerCase()
        && String(draftActiveModelId || '') === String(modelId || '');

    const canConfirmActiveModelSelection = useMemo(() => (
        !!String(draftActiveModelProvider || '').trim()
        && !!String(draftActiveModelId || '').trim()
        && !draftProviderModelsLoading
        && !saving
    ), [draftActiveModelProvider, draftActiveModelId, draftProviderModelsLoading, saving]);

    const isActiveWebSearchDraftSelected = (providerId) =>
        String(draftActiveWebSearchProvider || '').toLowerCase() === String(providerId || '').toLowerCase();

    const isActiveSelectionDirty = () => (
        (userSettings.activeModelId || '') !== (originalUserSettings.activeModelId || '')
        || (userSettings.activeModelProvider || '') !== (originalUserSettings.activeModelProvider || '')
        || (userSettings.activeWebSearchProvider || '') !== (originalUserSettings.activeWebSearchProvider || '')
    );

    const handleFeelLuckyMcpServerChange = (serverId) => {
        setUserSettings((prev) => ({ ...prev, feelLuckyMcpServerId: serverId || '' }));
    };

    const handleWebSearchKeyChange = (keyName, value) => {
        setUserSettings(prev => ({
            ...prev,
            web_search_keys: { ...(prev.web_search_keys || {}), [keyName]: typeof value === 'string' ? value : '' }
        }));
    };

    const buildWebSearchSavePayload = () => {
        const payload = {};
        const currentWeb = userSettings.web_search_keys || {};
        const originalWeb = originalUserSettings.web_search_keys || {};
        for (const provider of WEB_SEARCH_PROVIDERS) {
            const keyName = provider.keyName;
            if (provider.toggleKey) {
                const newVal = isFeelLuckyEnabled(currentWeb[keyName]) ? 'y' : 'n';
                const oldVal = isFeelLuckyEnabled(originalWeb[keyName]) ? 'y' : 'n';
                if (newVal !== oldVal) payload[keyName] = newVal;
                continue;
            }
            const newVal = typeof currentWeb[keyName] === 'string' ? currentWeb[keyName].trim() : '';
            const oldVal = typeof originalWeb[keyName] === 'string' ? originalWeb[keyName].trim() : '';
            if (newVal === oldVal) continue;
            if (isMasked(newVal)) continue;
            if (!newVal && !hasStoredKey(oldVal)) continue;
            payload[keyName] = newVal;
        }
        return payload;
    };

    const handleClearLlmKey = (provider) => {
        handleLlmKeyChange(provider, '');
    };

    const handleClearWebSearchKey = (keyName) => {
        handleWebSearchKeyChange(keyName, '');
    };

    const handleLlmKeyChange = (provider, value) => {
        const keyName = providerToKeyName(provider);
        const providerStateKey = getProviderId(provider) || getProviderSlug(provider);
        setUserSettings(prev => ({
            ...prev,
            llm_keys: {...prev.llm_keys, [keyName]: value}
        }));
        // If the value deviates from the last saved value, clear verification status to avoid confusion
        try {
            const originalVal = (originalUserSettings.llm_keys || {})[keyName] || '';
            const nowDirty = value !== originalVal;
            if (nowDirty) {
                setProviderSupport(prev => ({
                    ...prev,
                    [providerStateKey]: { loading: false, supported: null, status: null, message: '' }
                }));
            }
        } catch (_) {
            // noop
        }
    };

    const handlePansynapseUserIdChange = (value) => {
        setUserSettings((prev) => ({
            ...prev,
            pansynapseUserId: typeof value === 'string' ? value : '',
        }));
    };

    const handleLlmKeyBlur = async (provider) => {
        if (!userProviderKeysApiAvailable) return;
        const providerId = getProviderId(provider);
        const keyName = providerToKeyName(provider);
        const candidate = (userSettings.llm_keys || {})[keyName] || '';
        if (!providerId || !hasValue(candidate) || isMasked(candidate)) return;

        setProviderSupport(prev => ({
            ...prev,
            [providerId]: { ...(prev[providerId] || {}), loading: true }
        }));

        try {
            const validation = await validateUserAiProviderKey({ providerId, apiKey: candidate });
            setProviderSupport(prev => ({
                ...prev,
                [providerId]: {
                    loading: false,
                    supported: validation.status === 'valid',
                    status: validation.status,
                    message: validation.error || validation.message || ''
                }
            }));
        } catch (e) {
            setProviderSupport(prev => ({
                ...prev,
                [providerId]: {
                    loading: false,
                    supported: false,
                    status: 'error',
                    message: e.message || 'Validation failed'
                }
            }));
        }
    };

    // Provider support check
    const checkProviderSupport = async (provider) => {
        const providerId = getProviderId(provider) || getProviderSlug(provider);
        const providerSlug = getProviderSlug(provider);
        if (!providerSlug) return;
        const supportProvider = providerSlug === 'google' ? 'gemini' : providerSlug;
        setProviderSupport(prev => ({
            ...prev,
            [providerId]: { ...(prev[providerId] || {}), loading: true }
        }));
        try {
            const headers = await getAuthHeaders();
            const resp = await fetch(`${API_BASE_URL}/casc/v1/api/providers/check`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ provider: supportProvider })
            });
            if (resp.ok) {
                const data = await resp.json();
                const supported = !!data.supported;
                setProviderSupport(prev => ({
                    ...prev,
                    [providerId]: { loading: false, supported, status: supported ? 'valid' : 'invalid', message: '' }
                }));
            } else {
                // Treat as unsupported on error status
                setProviderSupport(prev => ({
                    ...prev,
                    [providerId]: { loading: false, supported: false, status: 'error', message: '' }
                }));
            }
        } catch (e) {
            // Network errors: mark as unsupported per API behavior guidance
            setProviderSupport(prev => ({
                ...prev,
                [providerId]: { loading: false, supported: false, status: 'error', message: '' }
            }));
        }
    };

    // When providers and user settings are loaded, run support checks for providers with filled keys
    // Skip providers whose keys are modified (dirty) and not yet saved; prompt user to save to verify
    useEffect(() => {
        if (!providers || providers.length === 0) return;
        const keys = userSettings?.llm_keys || {};
        providers.forEach((provider) => {
            const keyName = providerToKeyName(provider);
            const val = keys[keyName];
            const dirty = isProviderDirty(provider);
            if (hasValue(val) && !dirty) {
                // Fire and forget; internal state handles loading
                checkProviderSupport(provider);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providers, userSettings.llm_keys]);

    // Save System Settings
    const handleSaveSystemSettings = async () => {
        if (!user || !user.email) {
            setMessage({
                type: 'error',
                text: 'Cannot save settings: user not loaded. Please wait for the page to load.'
            });
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
            return;
        }

        setSaving(true);
        let hasError = false;

        try {
            // Save embedding model if changed (do NOT auto-save elsewhere)
            if (systemSettings.embeddingModel !== originalSystemSettings.embeddingModel) {
                const modelId = systemSettings.embeddingModel || '';
                // Optional: check access before saving
                try {
                    const checkHeaders = await getAuthHeaders();
                    await fetch(`${API_BASE_URL}/casc/v1/api/models/embedding`, {
                        method: 'POST', headers: checkHeaders,
                        body: JSON.stringify({ action: 'check', modelId })
                    });
                } catch (_) { /* ignore; backend also validates on save */ }

                const headers = await getAuthHeaders();
                const resp = await fetch(`${API_BASE_URL}/casc/v1/api/system-settings/set`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        key: 'EMBEDDING_MODEL',
                        value: modelId,
                        category: 'ai',
                        description: 'Selected embedding model',
                        isSecret: false
                    })
                });
                if (!resp.ok) {
                    hasError = true;
                    const t = await resp.text();
                    throw new Error(`Failed to save EMBEDDING_MODEL (${resp.status}): ${t}`);
                }
                // After successful save, update originals
                setOriginalSystemSettings(prev => ({ ...prev, embeddingModel: modelId }));
            }

            if (!hasError) {
                setMessage({type: 'success', text: 'System settings saved successfully!'});
                setTimeout(() => setMessage({type: '', text: ''}), 3000);
            }
        } catch (error) {
            console.error('Error saving system settings:', error);
            setMessage({type: 'error', text: error.message || 'Error saving system settings'});
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
        } finally {
            setSaving(false);
        }
    };

    const isPansynapseUserIdDirty = () =>
        (userSettings.pansynapseUserId || '') !== (originalUserSettings.pansynapseUserId || '');

    const savePansynapseUserIdIfChanged = async () => {
        if (!isPansynapseUserIdDirty()) return true;
        const headers = await getAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/casc/v1/api/user/settings`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                email: user.email,
                pansynapseUserId: userSettings.pansynapseUserId || '',
            }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data?.success === false) {
            throw new Error(data?.error || data?.message || 'Failed to save Pansynapse user ID');
        }
        const savedId = data.settings?.pansynapseUserId ?? userSettings.pansynapseUserId ?? '';
        setUserSettings((prev) => ({ ...prev, pansynapseUserId: savedId }));
        setOriginalUserSettings((prev) => ({ ...prev, pansynapseUserId: savedId }));
        return true;
    };

    // Save User Settings
    const handleSaveUserSettings = async () => {
        if (!user || !user.email) {
            setMessage({
                type: 'error',
                text: 'Cannot save settings: user not loaded. Please wait for the page to load.'
            });
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
            return;
        }

        setSaving(true);
        let hasError = false;

        try {
            // Save user settings if changed (only send non-masked values)
            const legacyChanged =
                userSettings.openaiKey !== originalUserSettings.openaiKey ||
                userSettings.geminiKey !== originalUserSettings.geminiKey ||
                userSettings.anthropicKey !== originalUserSettings.anthropicKey;

            // Compare llm_keys changes for known providers
            const llmKeysChanged = (() => {
                const a = userSettings.llm_keys || {};
                const b = originalUserSettings.llm_keys || {};
                // If providers array is empty, still check all keys present
                const keys = new Set([
                    ...Object.keys(a),
                    ...Object.keys(b),
                    ...providers.map(p => providerToKeyName(p))
                ]);
                for (const k of keys) {
                    if ((a[k] || '') !== (b[k] || '')) return true;
                }
                return false;
            })();

            const webSearchKeysChanged = (() => {
                const a = userSettings.web_search_keys || {};
                const b = originalUserSettings.web_search_keys || {};
                const keys = new Set([
                    ...Object.keys(a),
                    ...Object.keys(b),
                    ...WEB_SEARCH_PROVIDERS.map((p) => p.keyName)
                ]);
                for (const k of keys) {
                    if ((a[k] || '') !== (b[k] || '')) return true;
                }
                return false;
            })();

            const feelLuckyMcpChanged =
                (userSettings.feelLuckyMcpServerId || '') !== (originalUserSettings.feelLuckyMcpServerId || '');

            const pansynapseUserIdChanged = isPansynapseUserIdDirty();

            const researchTimeoutChanged =
                Number(userSettings.researchTimeoutMinutes) !== Number(originalUserSettings.researchTimeoutMinutes);
            const chatToolFieldLenChanged =
                Number(userSettings.chatToolResultMaxFieldLength)
                !== Number(originalUserSettings.chatToolResultMaxFieldLength);

            const activeSelectionChanged = isActiveSelectionDirty();

            if (legacyChanged || llmKeysChanged || webSearchKeysChanged || feelLuckyMcpChanged || pansynapseUserIdChanged || researchTimeoutChanged || chatToolFieldLenChanged || activeSelectionChanged) {
                // Preferred path: provider-key API by enabled provider registry entries
                if (userProviderKeysApiAvailable && providers.length > 0) {
                    const current = userSettings.llm_keys || {};
                    const original = originalUserSettings.llm_keys || {};
                    const changedProviders = [];

                    for (const provider of providers) {
                        const providerId = getProviderId(provider);
                        const keyName = providerToKeyName(provider);
                        const newVal = typeof current[keyName] === 'string' ? current[keyName] : '';
                        const oldVal = typeof original[keyName] === 'string' ? original[keyName] : '';
                        if (newVal === oldVal) continue;
                        if (!providerId) continue;
                        if (isMasked(newVal)) continue;

                        if (!hasValue(newVal)) {
                            if (!hasStoredKey(oldVal)) continue;
                            const deleteResult = await deleteUserAiProviderKey(providerId);
                            if (!deleteResult?.success) {
                                hasError = true;
                                throw new Error(`Failed to delete key for ${getProviderDisplayName(provider)}`);
                            }
                        } else {
                            const saveResult = await saveUserAiProviderKey(providerId, newVal);
                            if (!saveResult?.success) {
                                hasError = true;
                                throw new Error(`Failed to save key for ${getProviderDisplayName(provider)}`);
                            }
                        }

                        changedProviders.push(provider);
                    }

                    if (changedProviders.length > 0) {
                        const keysResponse = await listUserAiProviderKeys({ includeDisabledProviders: false });
                        const byProviderId = new Map();
                        const byProviderSlug = new Map();

                        (keysResponse.keys || []).forEach((item) => {
                            if (item?.providerId) byProviderId.set(item.providerId, item);
                            const slug = String(item?.provider?.slug || '').toLowerCase();
                            if (slug) byProviderSlug.set(slug, item);
                        });

                        const mappedLlmKeys = {};
                        providers.forEach((provider) => {
                            const providerId = getProviderId(provider);
                            const providerSlug = getProviderSlug(provider);
                            const keyName = providerToKeyName(provider);
                            const record = byProviderId.get(providerId) || byProviderSlug.get(providerSlug);
                            mappedLlmKeys[keyName] = record?.maskedKey || '';
                        });

                        const updated = {
                            openaiKey: mappedLlmKeys.OPENAI_KEY || '',
                            geminiKey: mappedLlmKeys.GEMINI_KEY || mappedLlmKeys.GOOGLE_KEY || '',
                            anthropicKey: mappedLlmKeys.ANTHROPIC_KEY || '',
                            llm_keys: mappedLlmKeys,
                            web_search_keys: userSettings.web_search_keys || {},
                            feelLuckyMcpServerId: userSettings.feelLuckyMcpServerId || '',
                            pansynapseUserId: userSettings.pansynapseUserId || '',
                            activeModelId: userSettings.activeModelId || '',
                            activeModelProvider: userSettings.activeModelProvider || '',
                            activeWebSearchProvider: userSettings.activeWebSearchProvider || '',
                            researchTimeoutMinutes: userSettings.researchTimeoutMinutes ?? RESEARCH_TIMEOUT_DEFAULT_MINUTES,
                            chatToolResultMaxFieldLength:
                                userSettings.chatToolResultMaxFieldLength ?? CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT,
                        };

                        setUserSettings(updated);
                        setOriginalUserSettings(updated);
                        changedProviders.forEach((provider) => checkProviderSupport(provider));
                    }

                    if (webSearchKeysChanged || feelLuckyMcpChanged || pansynapseUserIdChanged || researchTimeoutChanged || chatToolFieldLenChanged) {
                        const newWebSearchPayload = buildWebSearchSavePayload();
                        const settingsPayload = { email: user.email };
                        if (Object.keys(newWebSearchPayload).length > 0) {
                            settingsPayload.web_search_keys = newWebSearchPayload;
                        } else if (webSearchKeysChanged) {
                            hasError = true;
                            throw new Error(
                                'Web search API key was not saved. Paste the full key from your provider (do not re-save the masked placeholder like abc...wxyz).'
                            );
                        }
                        if (feelLuckyMcpChanged) {
                            settingsPayload.feelLuckyMcpServerId = userSettings.feelLuckyMcpServerId || '';
                        }
                        if (pansynapseUserIdChanged) {
                            settingsPayload.pansynapseUserId = userSettings.pansynapseUserId || '';
                        }
                        if (researchTimeoutChanged) {
                            settingsPayload.researchTimeoutMinutes = Number(userSettings.researchTimeoutMinutes) || RESEARCH_TIMEOUT_DEFAULT_MINUTES;
                        }
                        if (chatToolFieldLenChanged) {
                            settingsPayload.chatToolResultMaxFieldLength =
                                Number(userSettings.chatToolResultMaxFieldLength) || CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT;
                        }
                        if (Object.keys(settingsPayload).length > 1) {
                            const headers = await getAuthHeaders();
                            const response = await fetch(`${API_BASE_URL}/casc/v1/api/user/settings`, {
                                method: 'POST',
                                headers,
                                body: JSON.stringify(settingsPayload),
                            });
                            const data = await response.json().catch(() => ({}));
                            if (!response.ok || data?.success === false) {
                                hasError = true;
                                throw new Error(data?.error || data?.message || 'Failed to save web search / MCP settings');
                            }
                            const apiSettings = data.settings || {};
                            setUserSettings((prev) => ({
                                ...prev,
                                web_search_keys: apiSettings.web_search_keys || prev.web_search_keys || {},
                                feelLuckyMcpServerId: apiSettings.feelLuckyMcpServerId ?? prev.feelLuckyMcpServerId ?? '',
                                pansynapseUserId: apiSettings.pansynapseUserId ?? prev.pansynapseUserId ?? '',
                                researchTimeoutMinutes: apiSettings.researchTimeoutMinutes ?? prev.researchTimeoutMinutes ?? RESEARCH_TIMEOUT_DEFAULT_MINUTES,
                                chatToolResultMaxFieldLength:
                                    apiSettings.chatToolResultMaxFieldLength
                                    ?? prev.chatToolResultMaxFieldLength
                                    ?? CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT,
                            }));
                            setOriginalUserSettings((prev) => ({
                                ...prev,
                                web_search_keys: apiSettings.web_search_keys || prev.web_search_keys || {},
                                feelLuckyMcpServerId: apiSettings.feelLuckyMcpServerId ?? prev.feelLuckyMcpServerId ?? '',
                                pansynapseUserId: apiSettings.pansynapseUserId ?? prev.pansynapseUserId ?? '',
                                researchTimeoutMinutes: apiSettings.researchTimeoutMinutes ?? prev.researchTimeoutMinutes ?? RESEARCH_TIMEOUT_DEFAULT_MINUTES,
                                chatToolResultMaxFieldLength:
                                    apiSettings.chatToolResultMaxFieldLength
                                    ?? prev.chatToolResultMaxFieldLength
                                    ?? CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT,
                            }));
                        }
                    } else if (pansynapseUserIdChanged) {
                        await savePansynapseUserIdIfChanged();
                    } else if (researchTimeoutChanged || chatToolFieldLenChanged) {
                        const headers = await getAuthHeaders();
                        const body = { email: user.email };
                        if (researchTimeoutChanged) {
                            body.researchTimeoutMinutes =
                                Number(userSettings.researchTimeoutMinutes) || RESEARCH_TIMEOUT_DEFAULT_MINUTES;
                        }
                        if (chatToolFieldLenChanged) {
                            body.chatToolResultMaxFieldLength =
                                Number(userSettings.chatToolResultMaxFieldLength) || CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT;
                        }
                        const response = await fetch(`${API_BASE_URL}/casc/v1/api/user/settings`, {
                            method: 'POST',
                            headers,
                            body: JSON.stringify(body),
                        });
                        const data = await response.json().catch(() => ({}));
                        if (!response.ok || data?.success === false) {
                            hasError = true;
                            throw new Error(data?.error || data?.message || 'Failed to save user settings');
                        }
                        const mins = data.settings?.researchTimeoutMinutes ?? userSettings.researchTimeoutMinutes ?? RESEARCH_TIMEOUT_DEFAULT_MINUTES;
                        const fieldLen =
                            data.settings?.chatToolResultMaxFieldLength
                            ?? userSettings.chatToolResultMaxFieldLength
                            ?? CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT;
                        setUserSettings((prev) => ({
                            ...prev,
                            researchTimeoutMinutes: mins,
                            chatToolResultMaxFieldLength: fieldLen,
                        }));
                        setOriginalUserSettings((prev) => ({
                            ...prev,
                            researchTimeoutMinutes: mins,
                            chatToolResultMaxFieldLength: fieldLen,
                        }));
                    }

                } else {
                    // Legacy fallback path when provider-key API is unavailable
                    const payload = {};
                    if (userSettings.openaiKey !== originalUserSettings.openaiKey && userSettings.openaiKey && !isMasked(userSettings.openaiKey)) {
                        payload.openaiKey = userSettings.openaiKey;
                    }
                    if (userSettings.geminiKey !== originalUserSettings.geminiKey && userSettings.geminiKey && !isMasked(userSettings.geminiKey)) {
                        payload.geminiKey = userSettings.geminiKey;
                    }
                    if (userSettings.anthropicKey !== originalUserSettings.anthropicKey && userSettings.anthropicKey && !isMasked(userSettings.anthropicKey)) {
                        payload.anthropicKey = userSettings.anthropicKey;
                    }

                    const newLlmPayload = {};
                    const current = userSettings.llm_keys || {};
                    const original = originalUserSettings.llm_keys || {};
                    const targetProviders = providers.length > 0 ? providers : Object.keys(current).map(k => k.replace(/_KEY$/, '').toLowerCase());
                    for (const provider of targetProviders) {
                        const keyName = providerToKeyName(provider);
                        const newVal = typeof current[keyName] === 'string' ? current[keyName] : '';
                        const oldVal = typeof original[keyName] === 'string' ? original[keyName] : '';
                        if (newVal === oldVal) continue;
                        if (isMasked(newVal)) continue;
                        if (!newVal && !hasStoredKey(oldVal)) continue;
                        newLlmPayload[keyName] = newVal;
                    }
                    if (Object.keys(newLlmPayload).length > 0) {
                        payload.llm_keys = newLlmPayload;
                    }

                    const newWebSearchPayload = buildWebSearchSavePayload();
                    if (Object.keys(newWebSearchPayload).length > 0) {
                        payload.web_search_keys = newWebSearchPayload;
                    } else if (webSearchKeysChanged) {
                        hasError = true;
                        throw new Error(
                            'Web search API key was not saved. Paste the full key from your provider (do not re-save the masked placeholder like abc...wxyz).'
                        );
                    }

                    if (feelLuckyMcpChanged) {
                        payload.feelLuckyMcpServerId = userSettings.feelLuckyMcpServerId || '';
                    }

                    if (pansynapseUserIdChanged) {
                        payload.pansynapseUserId = userSettings.pansynapseUserId || '';
                    }

                    if (researchTimeoutChanged) {
                        payload.researchTimeoutMinutes = Number(userSettings.researchTimeoutMinutes) || RESEARCH_TIMEOUT_DEFAULT_MINUTES;
                    }
                    if (chatToolFieldLenChanged) {
                        payload.chatToolResultMaxFieldLength =
                            Number(userSettings.chatToolResultMaxFieldLength) || CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT;
                    }

                    if (Object.keys(payload).length > 0) {
                        const headers = await getAuthHeaders();
                        const response = await fetch(`${API_BASE_URL}/casc/v1/api/user/settings`, {
                            method: 'POST',
                            headers,
                            body: JSON.stringify({...payload, email: user.email}),
                        });

                        if (response.ok) {
                            const data = await response.json();
                            const apiSettings = data.settings || {};
                            const updated = {
                                openaiKey: apiSettings.openaiKey || userSettings.openaiKey,
                                geminiKey: apiSettings.geminiKey || userSettings.geminiKey,
                                anthropicKey: apiSettings.anthropicKey || userSettings.anthropicKey,
                                llm_keys: apiSettings.llm_keys || userSettings.llm_keys,
                                web_search_keys: apiSettings.web_search_keys || userSettings.web_search_keys,
                                feelLuckyMcpServerId: apiSettings.feelLuckyMcpServerId ?? userSettings.feelLuckyMcpServerId ?? '',
                                pansynapseUserId: apiSettings.pansynapseUserId ?? userSettings.pansynapseUserId ?? '',
                                activeModelId: apiSettings.activeModelId ?? userSettings.activeModelId ?? '',
                                activeModelProvider: apiSettings.activeModelProvider ?? userSettings.activeModelProvider ?? '',
                                activeWebSearchProvider: apiSettings.activeWebSearchProvider ?? userSettings.activeWebSearchProvider ?? '',
                                researchTimeoutMinutes: apiSettings.researchTimeoutMinutes ?? userSettings.researchTimeoutMinutes ?? RESEARCH_TIMEOUT_DEFAULT_MINUTES,
                                chatToolResultMaxFieldLength:
                                    apiSettings.chatToolResultMaxFieldLength
                                    ?? userSettings.chatToolResultMaxFieldLength
                                    ?? CHAT_TOOL_RESULT_MAX_FIELD_LENGTH_DEFAULT,
                            };
                            setUserSettings(updated);
                            setOriginalUserSettings(updated);
                        } else {
                            hasError = true;
                            const errorData = await response.json();
                            throw new Error(errorData.message || 'Failed to update user settings');
                        }
                    }
                }
            }

            if (activeSelectionChanged && !hasError) {
                await saveActiveUserSelections();
            }

            if (!hasError) {
                setMessage({type: 'success', text: 'User settings saved successfully!'});
                setTimeout(() => setMessage({type: '', text: ''}), 3000);
            }
        } catch (error) {
            console.error('Error saving user settings:', error);
            setMessage({type: 'error', text: error.message || 'Error saving user settings'});
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
        } finally {
            setSaving(false);
        }
    };

    // Save Data Settings
    const handleSaveDataSettings = async () => {
        if (!user || !user.email) {
            setMessage({
                type: 'error',
                text: 'Cannot save settings: user not loaded. Please wait for the page to load.'
            });
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
            return;
        }

        setSaving(true);
        let hasError = false;

        try {
            // Save active suffix if changed
            if (dataSettings.activeSuffix !== originalDataSettings.activeSuffix) {
                const headers = await getAuthHeaders();
                const response = await fetch(`${API_BASE_URL}/casc/v1/api/data-settings/set-active-suffix`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({suffix: dataSettings.activeSuffix}),
                });

                if (response.ok) {
                    setOriginalDataSettings({activeSuffix: dataSettings.activeSuffix});
                    // Horizon swap invalidates most client state; hard-reload after persist.
                    window.dispatchEvent(new CustomEvent('horizonChanged', {
                        detail: {
                            suffix: dataSettings.activeSuffix,
                            previousSuffix: originalDataSettings.activeSuffix,
                        },
                    }));
                    window.location.reload();
                    return;
                } else {
                    hasError = true;
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to save active suffix');
                }
            }

            if (!hasError) {
                setMessage({type: 'success', text: 'Data settings saved successfully!'});
                setTimeout(() => setMessage({type: '', text: ''}), 3000);
            }
        } catch (error) {
            console.error('Error saving data settings:', error);
            setMessage({type: 'error', text: error.message || 'Error saving data settings'});
            setTimeout(() => setMessage({type: '', text: ''}), 5000);
        } finally {
            setSaving(false);
        }
    };

    // Check if current tab has changes
    const hasTabChanges = () => {
        if (activeTab === 'system') {
            return (
                // Reflect embedding model differences or a pending selection before confirmation
                systemSettings.embeddingModel !== originalSystemSettings.embeddingModel ||
                (!!pendingEmbeddingModel && pendingEmbeddingModel !== originalSystemSettings.embeddingModel)
            );
        } else if (activeTab === 'user') {
            const legacyChanged = (
                userSettings.openaiKey !== originalUserSettings.openaiKey ||
                userSettings.geminiKey !== originalUserSettings.geminiKey ||
                userSettings.anthropicKey !== originalUserSettings.anthropicKey
            );
            const a = userSettings.llm_keys || {};
            const b = originalUserSettings.llm_keys || {};
            const keys = new Set([...Object.keys(a), ...Object.keys(b), ...providers.map(p => providerToKeyName(p))]);
            let llmChanged = false;
            keys.forEach(k => {
                if ((a[k] || '') !== (b[k] || '')) llmChanged = true;
            });
            const wa = userSettings.web_search_keys || {};
            const wb = originalUserSettings.web_search_keys || {};
            const webKeys = new Set([
                ...Object.keys(wa),
                ...Object.keys(wb),
                ...WEB_SEARCH_PROVIDERS.map((p) => p.keyName)
            ]);
            let webSearchChanged = false;
            webKeys.forEach(k => {
                if ((wa[k] || '') !== (wb[k] || '')) webSearchChanged = true;
            });
            return legacyChanged || llmChanged || webSearchChanged
                || (userSettings.feelLuckyMcpServerId || '') !== (originalUserSettings.feelLuckyMcpServerId || '')
                || (userSettings.pansynapseUserId || '') !== (originalUserSettings.pansynapseUserId || '')
                || Number(userSettings.researchTimeoutMinutes) !== Number(originalUserSettings.researchTimeoutMinutes)
                || Number(userSettings.chatToolResultMaxFieldLength) !== Number(originalUserSettings.chatToolResultMaxFieldLength)
                || isActiveSelectionDirty();
        }
        return false;
    };
    return { activeModelOptions, activeTab, applyActiveModelSelection, canConfirmActiveModelSelection, chatMarkdownEnabled, configuredLlmProviders, configuredWebSearchProviders, confirmDisconnectPersonalGoogle, darkMode, dataEntries, dataEntriesLoading, dataEntryForm, dataProcessingFieldForm, dataProcessingFields, dataProcessingFieldsLoading, dataSettings, dataSettingsLoading, draftActiveModelId, draftActiveModelProvider, draftActiveWebSearchProvider, draftProviderModels, draftProviderModelsError, draftProviderModelsLoading, editingDataEntry, editingDataProcessingField, embeddingEditGateAcknowledgedRef, embeddingModelHighlight, embeddingModelInputRef, embeddingModelListRef, embeddingModelQuery, embeddingModels, embeddingModelsLoading, filteredEmbeddingModels, feelLuckyMcpDirty: (userSettings.feelLuckyMcpServerId || '') !== (originalUserSettings.feelLuckyMcpServerId || ''), getActiveModelLabel, getActiveWebSearchLabel, getApiKeyPlaceholder, getApiTypeLabel, getProviderDisplayName, groupCtx, handleAddDataEntry, handleAddDataProcessingField, handleCancelActiveModelProviderWarning, handleClearLlmKey, handleClearWebSearchKey, handleCloseActiveModelPicker, handleConfirmActiveModelProviderWarning, handleConfirmActiveModelSelection, handleConfirmActiveWebSearchSelection, handleConnectPersonalGoogle, handleDeleteDataEntry, handleDeleteDataEntryClick, handleDeleteDataProcessingFieldClick, handleDiscardChanges, handleDiscardChangesClick, handleDisconnectPersonalGoogle, handleDraftActiveModelProviderChange, handleEditDataEntry, handleEditDataProcessingField, handleFeelLuckyMcpServerChange, handleImportUserGoogleCredentials, handleLlmKeyBlur, handleLlmKeyChange, handleOpenActiveModelPicker, handleOpenActiveWebSearchPicker, handlePansynapseUserIdChange, handleWebSearchKeyChange, handleSaveDataEntry, handleSaveDataProcessingField, handleSaveDataSettings, handleSaveSystemSettings, handleSaveUserGoogleCredentials, handleSaveUserSettings, handleSelectEmbeddingModel, handleTestApiKey, handleUpdateDatabase, handleUpdateDatabaseClick, handleUpdateProcessingDatabase, handleUserSettingsChange, hasDataEntryChanges, hasDataProcessingFieldChanges, hasStoredKey, hasTabChanges, hasValue, initialTab, isActiveModelDraftSelected, isActiveWebSearchDraftSelected, isMasked, isPansynapseUserIdDirty, isProviderDirty, isWebSearchKeyDirty, loadDataProcessingFields, message, pendingActiveModel, pendingChanges, pendingProcessingChanges, personalGoogleAuth, providerSupport, providerValidation, providerToKeyName, providers, runtimeInfo, saving, selectedGoogleCredentialImportId, setActiveTab, setDataEntryForm, setDataProcessingFieldForm, setDraftActiveModelId, setDraftActiveModelProvider, setDraftActiveWebSearchProvider, setEmbeddingModelHighlight, setEmbeddingModelQuery, setMessage, setSaving, setSelectedGoogleCredentialImportId, setShowActiveModelPicker, setShowActiveModelProviderWarning, setShowActiveWebSearchPicker, setShowCreateEntryTableConfirm, setShowCreateProcessingTableConfirm, setShowDataEntryModal, setShowDataProcessingFieldModal, setShowDeleteConfirm, setShowDiscardConfirm, setShowEmbeddingEditGate, setShowEmbeddingModelList, setShowPersonalDisconnectConfirm, setShowUpdateConfirm, setSystemSettings, setUserGoogleCredentials, showActiveModelPicker, showActiveModelProviderWarning, showActiveWebSearchPicker, showCreateEntryTableConfirm, showCreateProcessingTableConfirm, showDataEntryModal, showDataProcessingFieldModal, showDeleteConfirm, showDiscardConfirm, showEmbeddingEditGate, showEmbeddingModelList, showPersonalDisconnectConfirm, showUpdateConfirm, systemSettings, systemSettingsLoading, toggleChatMarkdown, toggleDarkMode, user, userGoogleCredentialImporting, userGoogleCredentialImports, userGoogleCredentialImportsLoading, userGoogleCredentials, userGoogleCredentialsSaving, userSettings, userSettingsLoading };
}
